import Swal from "sweetalert2";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { useAccountingV2 } from "./context/AccountingV2Context";
import { Modal, Form, Button, OverlayTrigger, Tooltip, Spinner, ProgressBar } from "react-bootstrap";
import { getRequest, postRequest, patchRequest, deleteRequest } from "services/api";
import Select from "react-select";
import BulkReservationDevTool from "./dev-tools/BulkReservationDevTool";
import "./accounting-v2.css";
import { getAllMasterKeep } from "./master-keep-api";
import { getAccountingTypeLabel, getAccountingTransactionText } from "./accounting-transaction-text";
import AccountingV2TrfModal from "./accounting-v2-trf-modal";
import AccountingV2BookkeepingSyncModal from "./accounting-v2-bookkeeping-sync-modal";
import { buildBulkFailureHtml, getBulkFailures, runChunkedBulkRequest } from "./bulk-api";

const evaluateAccountingFormula = (value) => {
  const input = String(value ?? "").trim();
  const hasFormulaPrefix = input.startsWith("=");
  const rawExpression = hasFormulaPrefix ? input.slice(1) : input;
  const expression = rawExpression.replace(/\s/g, "");
  const looksLikeFormula = hasFormulaPrefix || /[+\-*/()]/.test(expression) || /^SUM\(/i.test(expression);

  if (!looksLikeFormula) return parseNumberInput(input);

  try {
    if (expression.toUpperCase().startsWith("SUM(")) {
      if (!/^SUM\([0-9.,+\-*/()]+\)$/i.test(expression)) return null;
      const values = expression.slice(4, -1).split(",").map((item) => evaluateAccountingFormula(`=${item}`));
      return values.every(Number.isFinite) ? values.reduce((sum, value) => sum + value, 0) : null;
    }
    const normalizedExpression = expression.replace(/\./g, "").replace(/,/g, ".");
    if (!/^[0-9+\-*/().]+$/.test(normalizedExpression)) return null;
    const tokens = normalizedExpression.match(/\d+(?:\.\d+)?|[()+\-*/]/g);
    if (!tokens || tokens.join("") !== normalizedExpression) return null;

    const values = [];
    const operators = [];
    const precedence = { "+": 1, "-": 1, "*": 2, "/": 2 };

    const apply = () => {
      const operator = operators.pop();
      const right = values.pop();
      const left = values.pop();
      if ([operator, left, right].some((item) => item === undefined)) throw new Error("Invalid formula");
      const result = operator === "+"
        ? left + right
        : operator === "-"
          ? left - right
          : operator === "*"
            ? left * right
            : left / right;
      values.push(result);
    };

    let previousToken = null;
    tokens.forEach((token) => {
      if (!Number.isNaN(Number(token))) {
        values.push(Number(token));
        previousToken = "number";
        return;
      }

      if (token === "(") {
        operators.push(token);
        previousToken = "(";
        return;
      }

      if (token === ")") {
        while (operators.length && operators[operators.length - 1] !== "(") apply();
        if (operators.pop() !== "(") throw new Error("Invalid formula");
        previousToken = "number";
        return;
      }

      const isUnaryOperator = (token === "-" || token === "+") && (!previousToken || previousToken === "operator" || previousToken === "(");
      if (isUnaryOperator) values.push(0);

      while (operators.length && operators[operators.length - 1] !== "(" && precedence[operators[operators.length - 1]] >= precedence[token]) apply();
      operators.push(token);
      previousToken = "operator";
    });

    while (operators.length) {
      if (operators[operators.length - 1] === "(") throw new Error("Invalid formula");
      apply();
    }

    return values.length === 1 && Number.isFinite(values[0]) ? values[0] : null;
  } catch {
    return null;
  }
};

const getReservationIncomeId = (row) =>
  row?.reservation_income_id ||
  row?.reservation_income?.id ||
  row?.income?.id ||
  null;

const formatPrice = (value) =>
  Number(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

const parseNumberInput = (value) => {
  const normalized = String(value ?? "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getCreatedTime = (item) => {
  const raw = item?.created_at || item?.inserted_at || item?.updated_at || "";
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Terlama di atas, terbaru di bawah (dipakai di tabel per payment method).
const sortByCreatedAsc = (items = []) =>
  [...items].sort((a, b) => getCreatedTime(a) - getCreatedTime(b));

const getAccountingRowSearchText = (row) => {
  const activityText = row?.activities
    ?.map((activity) => `${activity.activity || ""} ${activity.qty || ""}`)
    .join(" ");

  return [
    row?.code,
    row?.reservation_code,
    row?.guest_transport,
    row?.guest_name,
    row?.hotel,
    row?.agent_name,
    row?.driver_name,
    row?.driver?.name,
    row?.driver,
    row?.type,
    activityText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const hasMeaningfulDate = (value) => {
  if (!value) return false;
  const time = new Date(value).getTime();
  return !Number.isNaN(time);
};

const hasMeaningfulValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

// The completed marker must come from the backend's last_updated field.
// Other fields such as first_edited/last_number can exist for unrelated
// bookkeeping metadata and must not make a row appear completed.
const getAccountingEditMarker = (row) =>
  row?.last_updated ??
  row?.reservation_income?.last_updated ??
  row?.reservation_income_last_updated ??
  row?.accounting_last_updated ??
  row?.income_last_updated;

const isAccountingRowEdited = (row) =>
  Boolean(hasMeaningfulValue(getAccountingEditMarker(row)));

const getPaymentItemEditMarker = (paymentItem) =>
  paymentItem?.last_updated ||
  paymentItem?.accounting_last_updated ||
  paymentItem?.last_number ||
  paymentItem?.accounting_last_number;

const getPaymentTaxRate = (paymentItem) =>
  Number(paymentItem?.tax ?? paymentItem?.payment_method?.tax) || 0;

const getPaymentTaxAmount = (paymentItem, rawAmount, editedPaymentTaxes, editedPaymentTaxRates = {}) => {
  if (editedPaymentTaxes[paymentItem?.id] !== undefined) {
    return Number(editedPaymentTaxes[paymentItem.id]) || 0;
  }
  if (paymentItem?.tax_amount !== undefined && paymentItem?.tax_amount !== null) {
    return Number(paymentItem.tax_amount) || 0;
  }
  const taxRate = editedPaymentTaxRates[paymentItem?.id] ?? getPaymentTaxRate(paymentItem);
  return (rawAmount * taxRate) / 100;
};

const getPaymentTaxRateFromAmount = (rawAmount, taxAmount) =>
  rawAmount > 0 ? (Number(taxAmount) / rawAmount) * 100 : 0;

const getStoredNetKuberValue = (row) => {
  const directValue = Number(row?.net_kuber);
  if (Number.isFinite(directValue) && directValue !== 0) return directValue;

  return [
    row?.net_kuber_nam,
    row?.net_kuber_mm,
    row?.net_kuber_bm,
    row?.net_kuber_josh,
    row?.net_kuber_pj,
  ].reduce((sum, value) => sum + (Number(value) || 0), 0);
};

const getManualNetKuberValue = (row, editedRows) =>
  editedRows[row?.id]?.netKuber ?? getStoredNetKuberValue(row);

const getStoredPjQty = (row, totalMotor = 0) => {
  const explicitQty = Number(row?.pj ?? row?.pj_qty ?? row?.net_kuber_pj_qty);
  if (Number.isFinite(explicitQty) && explicitQty > 0) return explicitQty;

  const storedAmount = Number(row?.net_kuber_pj);
  if (Number.isFinite(storedAmount) && storedAmount > 0) return totalMotor || Math.round(storedAmount / 25000);

  const type = String(row?.type || "").toLowerCase();
  return type === "pj" || type === "josh" || type === "j" ? totalMotor : 0;
};

const getStoredBmmmQty = (row, totalMotor = 0) => {
  const explicitQty = Number(row?.bmmm ?? row?.bmmm_qty ?? row?.bm_mm_qty ?? row?.net_kuber_bmmm_qty);
  if (Number.isFinite(explicitQty) && explicitQty > 0) return explicitQty;

  const storedAmount = Number(row?.net_kuber_mm || 0) + Number(row?.net_kuber_bm || 0);
  if (Number.isFinite(storedAmount) && storedAmount > 0) return totalMotor || Math.round(storedAmount / 10000);

  const type = String(row?.type || "").toLowerCase();
  return type === "bm" || type === "mm" ? totalMotor : 0;
};

const isPaymentItemEdited = (
  paymentItem,
  _reservation,
  editedPayments,
  editedPaymentTaxes,
  editedPaymentTaxRates = {}
) =>
  Boolean(
    editedPayments[paymentItem?.id] !== undefined ||
      editedPaymentTaxes[paymentItem?.id] !== undefined ||
      editedPaymentTaxRates[paymentItem?.id] !== undefined ||
      hasMeaningfulValue(getPaymentItemEditMarker(paymentItem)) ||
      (hasMeaningfulDate(paymentItem?.updated_at) &&
        hasMeaningfulDate(paymentItem?.created_at) &&
        new Date(paymentItem.updated_at).getTime() !==
          new Date(paymentItem.created_at).getTime())
  );

const API_EXTRA_SECTION_BY_UI = {
  profit: "profit",
  lainLain: "lain_lain",
  biaya: "biaya",
  cashOnHand: "cash_on_hand_deduction",
  transport: "biaya_transport",
};

const UI_EXTRA_SECTION_BY_API = Object.entries(API_EXTRA_SECTION_BY_UI).reduce(
  (acc, [uiSection, apiSection]) => ({ ...acc, [apiSection]: uiSection }),
  {}
);

const createBlankExtraRow = (section, overrides = {}) => ({
  id: `local-${section}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  apiId: null,
  name: "",
  col1: 0,
  col2: 0,
  col3: 0,
  col4: 0,
  col5: 0,
  description: "",
  notes: "",
  ...overrides,
});

const normalizeAdditionalTransaction = (item, uiSection) =>
  createBlankExtraRow(uiSection, {
    id: item.id,
    apiId: item.id,
    name: item.name || "",
    col2: Number(item.amount) || 0,
    description: item.description || "",
    notes: item.notes || "",
    last_updated: item.last_updated,
  });

const getArrayData = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const AccountingV2Page = () => {
  const { 
    bookings, 
    updateFilter, 
    clearSortOrder,
    filterOptions,
    saveReservationIncome,
    createReservationPayment,
    updateReservationPayment,
    deleteReservationIncome,
    deleteReservationPayment,
    reorderReservationIncomes,
    resetReservationIncomeLastUpdated,
    allPaymentMethods,
    total,
    accountingSummary,
    accountingByPayment,
    accountingResume,
    keepTransaction,
    refetch,
  } = useAccountingV2();

  const selectedDate = filterOptions.filters.start_date;
  const dateObj = new Date(selectedDate);
  const formattedDateTitle = dateObj.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).toUpperCase();

  const shortDate = dateObj.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "2-digit"
  }).replace(/ /g, '-');

  const bottomTabYear = dateObj.getFullYear();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempDate, setTempDate] = useState(selectedDate);
  const [editedRows, setEditedRows] = useState({});
  const [savedRows, setSavedRows] = useState({});
  const [editedPayments, setEditedPayments] = useState({});
  const [editedPaymentTaxes, setEditedPaymentTaxes] = useState({});
  const [editedPaymentTaxRates, setEditedPaymentTaxRates] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    reservation_income_id: "",
    payment_method_id: "",
    amount: "",
    sourceRow: null
  });

  const [activeEditCell, setActiveEditCell] = useState(null);
  const [activeExtraCell, setActiveExtraCell] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const saveTimeoutRef = useRef(null);
  const lastSavedValues = useRef({});
  const lastSavedPayments = useRef({});
  const lastSavedPaymentTaxes = useRef({});
  const lastSavedPaymentTaxRates = useRef({});
  const rowRefs = useRef({});
  const quickSearchInputRef = useRef(null);
  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false);
  const [quickSearchQuery, setQuickSearchQuery] = useState("");
  const [quickSearchIndex, setQuickSearchIndex] = useState(0);
  const [quickSearchSide, setQuickSearchSide] = useState("right");
  const [draggedRowId, setDraggedRowId] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const [resettingRows, setResettingRows] = useState({});
  // Jumlah proses simpan yang sedang berjalan per baris (bisa lebih dari 1 sel sekaligus).
  const [savingRows, setSavingRows] = useState({});
  // Feedback singkat setelah simpan per baris: 'success' (hilang sendiri) atau 'error' (bertahan sampai disimpan ulang).
  const [rowSaveFeedback, setRowSaveFeedback] = useState({});
  const rowFeedbackTimers = useRef({});
  // Urutan sementara (optimistic) setelah drop, sampai data dari server ter-refetch.
  const [localRowOrder, setLocalRowOrder] = useState(null);
  const [selectedKeepRows, setSelectedKeepRows] = useState({});
  const [isKeepSelecting, setIsKeepSelecting] = useState(false);
  const [keepSelectionAnchorIndex, setKeepSelectionAnchorIndex] = useState(null);
  const [keepSelectionMode, setKeepSelectionMode] = useState("select");

  const [showKeepModal, setShowKeepModal] = useState(false);
  // Snapshot baris terpilih saat popup Trf dibuka (null = popup tertutup).
  const [trfModalRows, setTrfModalRows] = useState(null);
  const [showBookkeepingSync, setShowBookkeepingSync] = useState(false);
  const [keepForm, setKeepForm] = useState({
    reservation_income_id: "",
    master_keep_id: "",
    agent_id: "",
    payment_bill: "",
    net_agent: "",
    notes: "",
    sourceRow: null,
    sourceRows: []
  });
  const [masterKeepList, setMasterKeepList] = useState([]);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [newMasterName, setNewMasterName] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => {
      setIsOnline(false);
      triggerSaveStatus('error');
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const stopKeepSelection = () => {
      setIsKeepSelecting(false);
      keepSelectionBaseRef.current = null;
    };
    window.addEventListener("mouseup", stopKeepSelection);
    return () => window.removeEventListener("mouseup", stopKeepSelection);
  }, []);

  const accountingResumeData = useMemo(() => {
    const data = accountingResume?.data?.data || accountingResume?.data || {};
    return data.summary || data.resume || data;
  }, [accountingResume]);
  const isAccountingResumeFallback = !accountingResumeData || Object.keys(accountingResumeData).filter((key) => !["payment_methods", "paymentMethods", "by_payment", "accounting_by_payment", "is_fallback"].includes(key)).length === 0;
  const shouldUseLocalResumeFallback = isAccountingResumeFallback;

  const getResumeValue = (keys, fallback = 0) => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    const foundKey = keyList.find((key) => accountingResumeData?.[key] !== undefined && accountingResumeData?.[key] !== null);
    if (foundKey) return Number(accountingResumeData[foundKey]) || 0;
    return shouldUseLocalResumeFallback ? fallback : 0;
  };

  const accountingResumePayments = useMemo(() => {
    const data = accountingResume?.data?.data || accountingResume?.data || {};
    const payments = data.payment_breakdown || data.payment_methods || data.paymentMethods || data.by_payment || data.accounting_by_payment || accountingByPayment?.data || [];
    if (Array.isArray(payments)) return payments;
    return Object.entries(payments).map(([name, value]) => ({ payment_method: name, ...(value || {}) }));
  }, [accountingByPayment?.data, accountingResume]);

  useEffect(() => () => {
    Object.values(rowFeedbackTimers.current).forEach(clearTimeout);
  }, []);

  const setRowFeedback = (rowId, status) => {
    clearTimeout(rowFeedbackTimers.current[rowId]);
    setRowSaveFeedback((current) => {
      const next = { ...current };
      if (status) next[rowId] = status;
      else delete next[rowId];
      return next;
    });
    if (status === 'success') {
      rowFeedbackTimers.current[rowId] = setTimeout(() => setRowFeedback(rowId, null), 1600);
    }
  };

  const triggerSaveStatus = (status) => {
    setSaveStatus(status);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (status === 'success' || status === 'error') {
      saveTimeoutRef.current = setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const activateEditCell = (rowId, fieldName) => {
    const cellKey = `${rowId}-${fieldName}`;
    if (activeEditCell === cellKey) return;
    setActiveEditCell(cellKey);
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const input = document.querySelector(`[data-cell="${cellKey}"]`);
        if (input) {
          input.focus();
          input.select();
        }
      });
    });
  };

  // Mulai edit langsung dari ketikan pertama (seperti Excel): sel dibuat editable
  // secara sinkron lalu isinya diganti karakter yang diketik, supaya ketikan
  // berikutnya langsung tersambung tanpa ada angka yang hilang.
  const startEditingWithKey = (input, rowId, fieldName, key) => {
    flushSync(() => setActiveEditCell(`${rowId}-${fieldName}`));

    const setNativeValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    setNativeValue.call(input, key);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.setSelectionRange(key.length, key.length);
  };

  const activateExtraCell = (section, id, fieldName) => {
    const cellKey = `${section}-${id}-${fieldName}`;
    if (activeExtraCell === cellKey) return;
    setActiveExtraCell(cellKey);
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const input = document.querySelector(`[data-extra-cell="${cellKey}"]`);
        if (input) {
          input.focus();
          input.select();
        }
      });
    });
  };

  const isExtraCellActive = (section, id, fieldName) =>
    activeExtraCell === `${section}-${id}-${fieldName}`;

  const getExtraCurrencyValue = (section, item, fieldName) => {
    const value = item[fieldName] ?? 0;
    return isExtraCellActive(section, item.id, fieldName)
      ? value
      : formatPrice(parseNumberInput(value));
  };

  const handleExtraCurrencyKeyDown = (event, section, item) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveExtraSectionRow(section, item);
      setActiveExtraCell(null);
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      setActiveExtraCell(null);
      event.currentTarget.blur();
    }
  };

  // Extra Sections State
  const [extraSections, setExtraSections] = useState({
    discova: [{ id: 1, name: 'ATV 2 SINGLE/', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 }],
    profit: [{ id: 1, name: 'PROFIT LAIN ACTIVITIES', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 }],
    lainLain: [{ id: 1, name: 'TIPING', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 }],
    transport: [{ id: 1, name: 'JEMPUTAN', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 }],
    biaya: [
      { id: 1, name: '', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 },
      { id: 2, name: '', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 }
    ],
    cashOnHand: [{ id: 1, name: '', col1: 0, col2: 0, col3: 0, col4: 0, col5: 0 }]
  });
  const [extraSectionSaving, setExtraSectionSaving] = useState({});

  useEffect(() => {
    let isMounted = true;

    const loadAdditionalTransactions = async () => {
      try {
        const query = new URLSearchParams({
          start_date: selectedDate,
          end_date: selectedDate,
          all: "1",
        }).toString();
        const response = await getRequest(
          `/accounting/additional-transactions?${query}`
        );
        if (!isMounted || !response?.success) return;

        const grouped = getArrayData(response).reduce((acc, item) => {
          const uiSection = UI_EXTRA_SECTION_BY_API[item.section];
          if (!uiSection) return acc;
          if (!acc[uiSection]) acc[uiSection] = [];
          acc[uiSection].push(normalizeAdditionalTransaction(item, uiSection));
          return acc;
        }, {});

        setExtraSections((prev) => {
          const next = { ...prev };
          Object.keys(API_EXTRA_SECTION_BY_UI).forEach((section) => {
            next[section] = grouped[section]?.length
              ? grouped[section]
              : [createBlankExtraRow(section)];
          });
          return next;
        });
      } catch (error) {
        console.warn("Gagal memuat additional transactions", error);
      }
    };

    loadAdditionalTransactions();
    return () => {
      isMounted = false;
    };
  }, [selectedDate]);

  const handleExtraSectionChange = (section, id, field, value) => {
    setExtraSections(prev => ({
      ...prev,
      [section]: prev[section].map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const saveExtraSectionRow = async (section, item) => {
    const apiSection = API_EXTRA_SECTION_BY_UI[section];
    if (!apiSection) return;

    const name = String(item.name || "").trim();
    const amount = parseNumberInput(item.col2);
    if (!name && amount === 0) return;

    const savingKey = `${section}-${item.id}`;
    setExtraSectionSaving((prev) => ({ ...prev, [savingKey]: true }));
    triggerSaveStatus('saving');

    const payload = {
      date: selectedDate,
      section: apiSection,
      name: name || "-",
      amount,
      description: item.description || null,
      notes: item.notes || null,
    };

    try {
      const response = item.apiId
        ? await patchRequest(`/accounting/additional-transactions/${item.apiId}`, payload)
        : await postRequest("/accounting/additional-transactions", payload);

      const savedItem = response?.data || response;
      if (savedItem?.id) {
        setExtraSections((prev) => ({
          ...prev,
          [section]: prev[section].map((row) =>
            row.id === item.id
              ? normalizeAdditionalTransaction(savedItem, section)
              : row
          ),
        }));
      }
      triggerSaveStatus('success');
    } catch (error) {
      triggerSaveStatus('error');
      Swal.fire("Pemberitahuan", error.message || "Gagal menyimpan transaksi tambahan", "error");
    } finally {
      setExtraSectionSaving((prev) => {
        const next = { ...prev };
        delete next[savingKey];
        return next;
      });
    }
  };

  const addExtraSectionRow = (section) => {
    setExtraSections(prev => ({
      ...prev,
      [section]: [...prev[section], createBlankExtraRow(section)]
    }));
  };

  const removeExtraSectionRow = async (section, id) => {
    const item = extraSections[section]?.find((row) => row.id === id);
    if (item?.apiId) {
      try {
        await deleteRequest(`/accounting/additional-transactions/${item.apiId}`);
        triggerSaveStatus('success');
      } catch (error) {
        triggerSaveStatus('error');
        Swal.fire("Pemberitahuan", error.message || "Gagal menghapus transaksi tambahan", "error");
        return;
      }
    }

    setExtraSections(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.id !== id)
    }));
  };

  const getExtraSectionTotal = (section, col) => {
    return extraSections[section].reduce((acc, curr) => {
      if (col) {
        const val = parseNumberInput(curr[col]);
        return acc + (isNaN(val) ? 0 : val);
      } else {
        const sumCols = ['col1', 'col2', 'col3', 'col4', 'col5'].reduce((colAcc, c) => {
          const val = parseNumberInput(curr[c]);
          return colAcc + (isNaN(val) ? 0 : val);
        }, 0);
        return acc + sumCols;
      }
    }, 0);
  };

  const handleOpenDatePicker = () => {
    setTempDate(selectedDate);
    setShowDatePicker(true);
  };

  // Generate past days (e.g. 30 days) to display as tabs
  const pastDays = useMemo(() => {
    const days = [];
    const todayForTabs = new Date(); // Anchor today
    for (let i = 0; i < 30; i++) {
      const d = new Date(todayForTabs);
      d.setDate(d.getDate() - i);
      days.push(d);
    }
    return days;
  }, []);

  // Prepare flat data for the table
  const tableData = useMemo(() => {
    if (!bookings?.data) return [];
    if (!localRowOrder) return bookings.data;

    const orderIndex = new Map(localRowOrder.map((id, index) => [id, index]));
    return [...bookings.data].sort(
      (a, b) => (orderIndex.get(a.id) ?? Infinity) - (orderIndex.get(b.id) ?? Infinity)
    );
  }, [bookings, localRowOrder]);

  useEffect(() => {
    setLocalRowOrder(null);
  }, [bookings, selectedDate]);

  useEffect(() => {
    clearSortOrder?.();
  }, [clearSortOrder]);

  const buildReorderItems = (rows) =>
    rows.map((row, index) => {
      const reservationIncomeId = row.reservation_income_id || row.reservation_income?.id;
      return {
        ...(reservationIncomeId && { reservation_income_id: reservationIncomeId }),
        reservation_id: row.id,
        sort_order: index + 1,
      };
    });

  const saveTransactionOrder = (orderedRows) => {
    if (!reorderReservationIncomes || !selectedDate) return;
    triggerSaveStatus('saving');
    reorderReservationIncomes.mutate(
      {
        date: selectedDate,
        items: buildReorderItems(orderedRows),
      },
      {
        onSuccess: () => {
          triggerSaveStatus('success');
        },
        onError: (error) => {
          triggerSaveStatus('error');
          setLocalRowOrder(null);
          Swal.fire("Pemberitahuan", error.message || "Gagal menyimpan urutan transaksi", "error");
        },
      }
    );
  };

  const handleTransactionDrop = (targetRowId) => {
    setDragOverRowId(null);
    if (!draggedRowId || draggedRowId === targetRowId) return;

    const fromIndex = tableData.findIndex((row) => row.id === draggedRowId);
    const toIndex = tableData.findIndex((row) => row.id === targetRowId);
    if (fromIndex < 0 || toIndex < 0) return;

    const orderedRows = [...tableData];
    const [movedRow] = orderedRows.splice(fromIndex, 1);
    orderedRows.splice(toIndex, 0, movedRow);
    setLocalRowOrder(orderedRows.map((row) => row.id));
    saveTransactionOrder(orderedRows);
    setDraggedRowId(null);
  };

  const handleResetLastUpdated = async (row) => {
    const reservationIncomeId = getReservationIncomeId(row);
    if (!resetReservationIncomeLastUpdated || !reservationIncomeId) {
      Swal.fire("Pemberitahuan", "Transaksi ini belum memiliki reservation_income_id.", "warning");
      return;
    }

    const confirmation = await Swal.fire({
      title: "Hapus last updated?",
      text: "Transaksi akan kembali ditandai belum dikerjakan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmation.isConfirmed) return;

    triggerSaveStatus('saving');
    setResettingRows((current) => ({ ...current, [row.id]: true }));
    resetReservationIncomeLastUpdated.mutate(reservationIncomeId, {
      onSettled: () => {
        setResettingRows((current) => {
          const next = { ...current };
          delete next[row.id];
          return next;
        });
      },
      onSuccess: () => {
        triggerSaveStatus('success');
        setSavedRows((current) => {
          const next = { ...current };
          delete next[row.id];
          return next;
        });
        Object.keys(lastSavedValues.current).forEach((key) => {
          if (key.startsWith(`${row.id}-`)) delete lastSavedValues.current[key];
        });
      },
      onError: (error) => {
        triggerSaveStatus('error');
        Swal.fire("Pemberitahuan", error.message || "Gagal menghapus last updated", "error");
      },
    });
  };

  const getKeepRowsInRange = (fromIndex, toIndex) => {
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    return tableData.slice(start, end + 1).map((row) => ({
      ...row,
      transaksiText: getAccountingTransactionText(row),
    }));
  };

  // Pilihan sebelum mulai seret disimpan, supaya seret mundur mengurangi rentang dengan mulus
  // tanpa menghapus pilihan lain yang sudah ada.
  const keepSelectionBaseRef = useRef(null);

  const applyKeepSelectionRange = (fromIndex, toIndex, mode) => {
    const rowsInRange = getKeepRowsInRange(fromIndex, toIndex);
    setSelectedKeepRows((current) => {
      const next = { ...(keepSelectionBaseRef.current || current) };
      rowsInRange.forEach((row) => {
        if (mode === "deselect") delete next[row.id];
        else next[row.id] = row;
      });
      return next;
    });
  };

  const handleKeepSelectionStart = (event, index, row) => {
    event.preventDefault();
    event.stopPropagation();
    const mode = selectedKeepRows[row.id] ? "deselect" : "select";
    keepSelectionBaseRef.current = selectedKeepRows;
    setIsKeepSelecting(true);
    setKeepSelectionAnchorIndex(index);
    setKeepSelectionMode(mode);
    applyKeepSelectionRange(index, index, mode);
  };

  const handleKeepSelectionEnter = (index) => {
    if (!isKeepSelecting || keepSelectionAnchorIndex === null) return;
    applyKeepSelectionRange(keepSelectionAnchorIndex, index, keepSelectionMode);
  };

  const quickSearchMatches = useMemo(() => {
    const keyword = quickSearchQuery.trim().toLowerCase();
    if (!keyword) return [];

    return tableData
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => getAccountingRowSearchText(row).includes(keyword));
  }, [quickSearchQuery, tableData]);

  const activeQuickSearchRowId =
    quickSearchMatches[quickSearchIndex]?.row?.id || null;

  useEffect(() => {
    setQuickSearchIndex(0);
  }, [quickSearchQuery]);

  useEffect(() => {
    if (quickSearchIndex > 0 && quickSearchIndex >= quickSearchMatches.length) {
      setQuickSearchIndex(Math.max(quickSearchMatches.length - 1, 0));
    }
  }, [quickSearchIndex, quickSearchMatches.length]);

  useEffect(() => {
    if (!activeQuickSearchRowId) return;
    rowRefs.current[activeQuickSearchRowId]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [activeQuickSearchRowId]);

  useEffect(() => {
    const handleQuickSearchShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "f") {
        event.preventDefault();
        setIsQuickSearchOpen(true);
        setTimeout(() => quickSearchInputRef.current?.focus(), 0);
      }

      if (event.key === "Escape" && isQuickSearchOpen) {
        setIsQuickSearchOpen(false);
      }
    };

    window.addEventListener("keydown", handleQuickSearchShortcut);
    return () => window.removeEventListener("keydown", handleQuickSearchShortcut);
  }, [isQuickSearchOpen]);

  const moveQuickSearchMatch = (direction) => {
    if (!quickSearchMatches.length) return;
    setQuickSearchIndex((current) => {
      const next = current + direction;
      if (next < 0) return quickSearchMatches.length - 1;
      if (next >= quickSearchMatches.length) return 0;
      return next;
    });
  };

  // Handle arrow key navigation between inputs
  const handleKeyDown = (e, rowIdx, colName, rowId, rowData) => {
    const columns = ['transaksi', 'paymentBill', 'netAgent', 'susukAgent', 'netKuber', 'others', 'pj', 'bmmm'];
    const cellKey = `${rowId}-${colName}`;
    const isEditing = activeEditCell === cellKey;
    
    let nextRow = rowIdx;
    let nextCol = colName;
    
    if (isEditing) {
      if (e.key === 'Enter' || e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Tab') {
        e.preventDefault();
        const result = evaluateAccountingFormula(e.target.value);
        if (result !== null) handleBlur(rowId, colName, result, rowData);
        // Tutup mode edit secara sinkron supaya onBlur sel ini tidak menyimpan dua kali
        // saat fokus langsung dipindah ke sel berikutnya.
        flushSync(() => setActiveEditCell(null));
        if (e.key === 'ArrowUp') nextRow = rowIdx - 1;
        if (e.key === 'ArrowDown' || e.key === 'Enter') nextRow = rowIdx + 1;
        if (e.key === 'Tab') {
          const colIdx = columns.indexOf(colName);
          if (e.shiftKey) {
            if (colIdx > 0) nextCol = columns[colIdx - 1];
            else { nextRow = rowIdx - 1; nextCol = columns[columns.length - 1]; }
          } else {
            if (colIdx < columns.length - 1) nextCol = columns[colIdx + 1];
            else { nextRow = rowIdx + 1; nextCol = columns[0]; }
          }
        }
      } else {
        if (e.key === 'Escape') setActiveEditCell(null);
        return;
      }
    } else {
      if (e.key === 'Enter') {
        e.preventDefault();
        activateEditCell(rowId, colName);
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextRow = rowIdx - 1;
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextRow = rowIdx + 1;
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const colIdx = columns.indexOf(colName);
        if (colIdx > 0) nextCol = columns[colIdx - 1];
        else return;
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const colIdx = columns.indexOf(colName);
        if (colIdx < columns.length - 1) nextCol = columns[colIdx + 1];
        else return;
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const colIdx = columns.indexOf(colName);
        if (e.shiftKey) {
          if (colIdx > 0) nextCol = columns[colIdx - 1];
          else { nextRow = rowIdx - 1; nextCol = columns[columns.length - 1]; }
        } else {
          if (colIdx < columns.length - 1) nextCol = columns[colIdx + 1];
          else { nextRow = rowIdx + 1; nextCol = columns[0]; }
        }
      } else if (/^[a-zA-Z0-9]$/.test(e.key) && !e.ctrlKey && !e.metaKey) {
        if (colName !== 'transaksi') {
          e.preventDefault();
          startEditingWithKey(e.currentTarget, rowId, colName, e.key);
        }
        return;
      } else {
        return;
      }
    }
    
    if (nextRow !== rowIdx || nextCol !== colName) {
      const nextInput = document.querySelector(`input[data-row="${nextRow}"][data-col="${nextCol}"]`);
      if (nextInput) {
        nextInput.focus();
        
        // Smart Scroll: ensure it's not hidden behind sticky top/bottom bars
        const rect = nextInput.getBoundingClientRect();
        const bottomBarHeight = 90; // Height of accounting-v2-bottom-bar + some padding
        const topNavbarHeight = 80; // Height of top navbar
        
        if (rect.bottom > (window.innerHeight - bottomBarHeight)) {
          // Scroll down so the cell is fully visible above the bottom bar
          window.scrollBy({ top: rect.bottom - (window.innerHeight - bottomBarHeight), behavior: 'auto' });
        } else if (rect.top < topNavbarHeight) {
          // Scroll up so the cell is visible below the top navbar
          window.scrollBy({ top: rect.top - topNavbarHeight - 10, behavior: 'auto' });
        }

        setTimeout(() => nextInput.select(), 0);
      }
    }
  };

  const handleBlur = (rowId, fieldName, newVal, rowData) => {
    // Only fire if we actually have the mutation function
    if (!saveReservationIncome) return;

    // Get single and tandem qty to find totalMotor
    let totalMotor = 0;
    rowData.activities?.forEach(act => {
      if (act.activity?.includes("ATV")) totalMotor += act.qty;
    });

    const defaultPjQty = getStoredPjQty(rowData, totalMotor);
    const defaultBmmmQty = getStoredBmmmQty(rowData, totalMotor);
    const isClassificationField = fieldName === 'pj' || fieldName === 'bmmm';
    const rowAlreadyEdited = isAccountingRowEdited(rowData);
    // Nilai 0 pada PJ / BM MM yang sudah pernah disimpan tetap dihitung sebagai klasifikasi.
    const hasExistingClassification =
      defaultPjQty > 0 ||
      defaultBmmmQty > 0 ||
      rowAlreadyEdited ||
      lastSavedValues.current[`${rowId}-pj`] !== undefined ||
      lastSavedValues.current[`${rowId}-bmmm`] !== undefined;
    
    const pjQty = fieldName === 'pj' ? newVal : (editedRows[rowId]?.pj ?? defaultPjQty);
    const bmmmQty = fieldName === 'bmmm' ? newVal : (editedRows[rowId]?.bmmm ?? defaultBmmmQty);
    const netKuber = fieldName === 'netKuber' ? newVal : (editedRows[rowId]?.netKuber ?? getStoredNetKuberValue(rowData));

    let type = rowData.type?.toLowerCase();
    
    if (fieldName === 'pj' && newVal > 0) {
      type = 'josh';
    } else if (fieldName === 'bmmm' && newVal > 0) {
      type = (type === 'bm') ? 'bm' : 'mm';
    } else if (pjQty === 0 && bmmmQty === 0) {
      type = 'nam';
    } else if (!['nam', 'mm', 'bm', 'josh'].includes(type)) {
      type = 'nam';
    }

    const valPJ = pjQty * 25000;
    const valBMMM = bmmmQty * 10000;
    const paymentBill = fieldName === 'paymentBill' ? newVal : (editedRows[rowId]?.paymentBill ?? (Number(rowData.payment_bill) || 0));
    const susukAgent = fieldName === 'susukAgent' ? newVal : (editedRows[rowId]?.susukAgent ?? (Number(rowData.susuk_agent) || 0));
    const others = fieldName === 'others' ? newVal : (editedRows[rowId]?.others ?? (Number(rowData.others) || 0));
    const netAgentInput = fieldName === 'netAgent' ? newVal : editedRows[rowId]?.netAgent;
    
    const calculatedNetAgent = netKuber + others + valPJ + valBMMM;
    const finalNetAgent = netAgentInput !== undefined ? netAgentInput : (Number(rowData.net_agent) || calculatedNetAgent);

    // Calculate original value to prevent saving identical data
    let originalValue;
    if (fieldName === 'pj') originalValue = defaultPjQty;
    else if (fieldName === 'bmmm') originalValue = defaultBmmmQty;
    else if (fieldName === 'netKuber') originalValue = getStoredNetKuberValue(rowData);
    else if (fieldName === 'paymentBill') originalValue = Number(rowData.payment_bill) || 0;
    else if (fieldName === 'susukAgent') originalValue = Number(rowData.susuk_agent) || 0;
    else if (fieldName === 'others') originalValue = Number(rowData.others) || 0;
    else if (fieldName === 'netAgent') originalValue = Number(rowData.net_agent) || calculatedNetAgent;

    const cellKey = `${rowId}-${fieldName}`;
    
    // Prevent API spam if value hasn't actually changed since last save or from original
    if (lastSavedValues.current[cellKey] === newVal) return;
    // PJ / BM MM yang diisi 0 pada row yang belum dikerjakan tetap harus disimpan,
    // walaupun sama dengan nilai default (0).
    const isExplicitZeroClassification = isClassificationField && newVal === 0 && !rowAlreadyEdited;
    if (lastSavedValues.current[cellKey] === undefined && newVal === originalValue && !isExplicitZeroClassification) return;

    // Tahan autosave sampai kolom klasifikasi ATV (PJ / BM MM) terisi.
    // Ini mencegah backend mengisi marker first-edit terlalu awal dan row tersortir
    // sebelum admin selesai menentukan masuk PJ atau BM/MM.
    if (!isClassificationField && !hasExistingClassification && pjQty <= 0 && bmmmQty <= 0) return;

    lastSavedValues.current[cellKey] = newVal;

    const payload = {
      reservation_income_id: getReservationIncomeId(rowData),
      reservation_id: rowId,
      type: type,
      payment_bill: paymentBill,
      net_agent: finalNetAgent,
      susuk_agent: susukAgent,
      net_kuber: netKuber,
      others: others
    };

    triggerSaveStatus('saving');
    setRowFeedback(rowId, null);
    setSavingRows((current) => ({ ...current, [rowId]: (current[rowId] || 0) + 1 }));
    saveReservationIncome.mutate(payload, {
      onSettled: () => {
        setSavingRows((current) => {
          const next = { ...current };
          if ((next[rowId] || 0) <= 1) delete next[rowId];
          else next[rowId] -= 1;
          return next;
        });
      },
      onSuccess: () => {
        triggerSaveStatus('success');
        setRowFeedback(rowId, 'success');
        setSavedRows((current) => ({ ...current, [rowId]: true }));
      },
      onError: () => {
        triggerSaveStatus('error');
        setRowFeedback(rowId, 'error');
        // Reset last saved on error so they can try again
        delete lastSavedValues.current[cellKey];
      }
    });
  };

  const handlePaymentKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handlePaymentBlur = (paymentItem, newVal) => {
    if (!paymentItem.id || !updateReservationPayment) return;
    
    const originalAmount = Number(paymentItem.amount) || 0;
    const paymentKey = `payment-${paymentItem.id}`;
    
    // Prevent spam if not changed
    if (lastSavedPayments.current[paymentKey] === newVal) return;
    if (lastSavedPayments.current[paymentKey] === undefined && newVal === originalAmount) return;

    lastSavedPayments.current[paymentKey] = newVal;

    const lockedTaxAmount = editedPaymentTaxes[paymentItem.id] ?? paymentItem.tax_amount;
    const payload = {
      reservation_income_id: paymentItem.reservation_income_id,
      payment_method_id: paymentItem.payment_method_id,
      amount: newVal,
      tax: editedPaymentTaxRates[paymentItem.id] ??
        getPaymentTaxRateFromAmount(
          newVal,
          lockedTaxAmount ?? (newVal * getPaymentTaxRate(paymentItem)) / 100
        ),
      ...(lockedTaxAmount !== undefined && lockedTaxAmount !== null && { tax_amount: Number(lockedTaxAmount) || 0 }),
    };

    triggerSaveStatus('saving');
    updateReservationPayment.mutate({ id: paymentItem.id, payload }, {
      onSuccess: () => {
        triggerSaveStatus('success');
        setEditedPayments(prev => {
          const next = { ...prev };
          delete next[paymentItem.id];
          return next;
        });
      },
      onError: () => {
        triggerSaveStatus('error');
        delete lastSavedPayments.current[paymentKey];
      }
    });
  };

  const handlePaymentTaxBlur = (paymentItem, newVal) => {
    if (!paymentItem.id || !updateReservationPayment) return;

    const amount = editedPayments[paymentItem.id] ?? (Number(paymentItem.amount) || 0);
    const originalTax = (amount * getPaymentTaxRate(paymentItem)) / 100;
    const taxKey = `payment-tax-${paymentItem.id}`;

    if (lastSavedPaymentTaxes.current[taxKey] === newVal) return;
    if (lastSavedPaymentTaxes.current[taxKey] === undefined && newVal === originalTax) return;

    lastSavedPaymentTaxes.current[taxKey] = newVal;

    const payload = {
      reservation_income_id: paymentItem.reservation_income_id,
      payment_method_id: paymentItem.payment_method_id,
      amount,
      tax: getPaymentTaxRateFromAmount(amount, newVal),
      tax_amount: newVal,
    };

    triggerSaveStatus('saving');
    updateReservationPayment.mutate({ id: paymentItem.id, payload }, {
      onSuccess: () => {
        triggerSaveStatus('success');
      },
      onError: () => {
        triggerSaveStatus('error');
        delete lastSavedPaymentTaxes.current[taxKey];
      }
    });
  };

  const handlePaymentTaxRateBlur = (paymentItem, newVal) => {
    if (!paymentItem.id || !updateReservationPayment) return;

    const amount = editedPayments[paymentItem.id] ?? (Number(paymentItem.amount) || 0);
    const originalTaxRate = getPaymentTaxRate(paymentItem);
    const taxRateKey = `payment-tax-rate-${paymentItem.id}`;

    if (lastSavedPaymentTaxRates.current[taxRateKey] === newVal) return;
    if (lastSavedPaymentTaxRates.current[taxRateKey] === undefined && newVal === originalTaxRate) return;

    lastSavedPaymentTaxRates.current[taxRateKey] = newVal;

    const payload = {
      reservation_income_id: paymentItem.reservation_income_id,
      payment_method_id: paymentItem.payment_method_id,
      amount,
      tax: newVal,
      tax_amount: null,
    };

    triggerSaveStatus('saving');
    updateReservationPayment.mutate({ id: paymentItem.id, payload }, {
      onSuccess: () => {
        triggerSaveStatus('success');
      },
      onError: () => {
        triggerSaveStatus('error');
        delete lastSavedPaymentTaxRates.current[taxRateKey];
      }
    });
  };

  const handleOpenPaymentModal = (row, transaksiText) => {
    setPaymentForm({
      id: null,
      reservation_income_id: getReservationIncomeId(row),
      payment_method_id: "",
      amount: "",
      sourceRow: { ...row, transaksiText }
    });
    setShowPaymentModal(true);
  };

  const handleOpenEditPaymentModal = (paymentItem, transaksiText) => {
    setPaymentForm({
      id: paymentItem.id,
      reservation_income_id: paymentItem.reservation_income_id,
      payment_method_id: paymentItem.payment_method_id,
      amount: Number(paymentItem.amount),
      sourceRow: { transaksiText }
    });
    setShowPaymentModal(true);
  };

  const selectedKeepRowsList = Object.values(selectedKeepRows);

  const handleOpenSelectedKeepModal = () => {
    if (selectedKeepRowsList.length === 0) {
      Swal.fire("Pemberitahuan", "Pilih minimal 1 transaksi untuk di-keep.", "warning");
      return;
    }
    handleOpenKeepModal(tableData.filter((row) => selectedKeepRows[row.id]));
  };

  const handleDeleteIncome = async () => {
    const result = await Swal.fire({
      title: "Hapus transaksi?",
      text: "Data akunting untuk transaksi ini akan dihapus. Pastikan tidak sedang dipakai untuk laporan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#d33",
      reverseButtons: true,
      returnFocus: false,
    });

    if (!result.isConfirmed) return;

    deleteReservationIncome.mutate(paymentForm.reservation_income_id, {
      onSuccess: () => {
        setShowPaymentModal(false);
      },
      onError: (err) => {
        Swal.fire("Pemberitahuan", err.message || "Gagal menghapus transaksi", "error");
      }
    });
  };

  const handleDeletePayment = async () => {
    const result = await Swal.fire({
      title: "Hapus payment?",
      text: "Data payment ini akan dihapus dari resume transaksi.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
      confirmButtonColor: "#d33",
      reverseButtons: true,
      returnFocus: false,
    });

    if (!result.isConfirmed) return;

    deleteReservationPayment.mutate(paymentForm.id, {
      onSuccess: () => {
        setShowPaymentModal(false);
      },
      onError: (err) => {
        Swal.fire("Pemberitahuan", err.message || "Gagal menghapus payment", "error");
      }
    });
  };

  const handleOpenKeepModal = (sourceRow) => {
    const sourceRows = Array.isArray(sourceRow) ? sourceRow : [sourceRow];
    const invalidRow = sourceRows.find((row) => !getReservationIncomeId(row));
    const reservationIncomeId = getReservationIncomeId(sourceRows[0]);
    if (!reservationIncomeId) {
      Swal.fire("Data belum lengkap", "Transaksi ini belum memiliki reservation_income_id yang valid untuk disimpan ke Keep.", "warning");
      return;
    }
    if (invalidRow) {
      Swal.fire("Data belum lengkap", "Ada transaksi terpilih yang belum memiliki reservation_income_id valid untuk disimpan ke Keep.", "warning");
      return;
    }
    if (masterKeepList.length === 0) {
      getAllMasterKeep()
        .then(res => {
           if (res && res.success) setMasterKeepList(res.data);
        })
        .catch(err => {
           console.error("Gagal mengambil data master keep", err);
        });
    }
    
    setKeepForm({
      reservation_income_id: reservationIncomeId,
      agent_id: sourceRows[0].agent_id || sourceRows[0].agent?.id || "",
      master_keep_id: "",
      payment_bill: sourceRows.reduce((sum, row) => sum + Number(row.payment_bill || 0), 0),
      net_agent: sourceRows.reduce((sum, row) => sum + Number(row.net_agent || 0), 0),
      notes: "",
      sourceRow: sourceRows[0],
      sourceRows
    });
    setShowPaymentModal(false);
    setShowKeepModal(true);
  };

  const [keepProgress, setKeepProgress] = useState({ active: false, current: 0, total: 0 });

  const handleSubmitKeep = async (e) => {
    e.preventDefault();
    if (!keepForm.master_keep_id) {
       Swal.fire("Pemberitahuan", "Master Keep (PIC) wajib dipilih", "warning");
       return;
    }
    
    const rowsToKeep = keepForm.sourceRows?.length ? keepForm.sourceRows : [keepForm.sourceRow];
    const payloads = rowsToKeep
      .filter(Boolean)
      .map((row) => ({
        row_id: row.id,
        reservation_code: row.transaksiText || row.reservation?.code || row.code || row.id,
        // Teks transaksi lengkap persis seperti di Accounting V2 (sama seperti saat copy ke Trf),
        // supaya kolom TRANSAKSI di Keep tidak terpotong / berbeda.
        transaksi_text: getAccountingTransactionText(row),
        reservation_income_id: getReservationIncomeId(row),
        agent_id: row.agent_id || row.agent?.id || keepForm.agent_id || null,
        master_keep_id: keepForm.master_keep_id,
        payment_bill: row.payment_bill || 0,
        net_agent: row.net_agent || 0,
        notes: keepForm.notes || null,
      }));

    // 1 request POST /keep/bulk (dipecah per 500). master_keep_id & notes dikirim per item sesuai kontrak BE.
    setKeepProgress({ active: true, current: 0, total: payloads.length });
    let response;
    try {
      response = await runChunkedBulkRequest({
        url: "/keep/bulk",
        method: "POST",
        items: payloads,
        buildPayload: (chunk) => ({
          atomic: false,
          items: chunk.map(({ row_id: unusedRowId, reservation_code: unusedCode, ...item }) => item),
        }),
      });
    } catch (error) {
      setKeepProgress({ active: false, current: 0, total: 0 });
      Swal.fire("Gagal", error?.message || "Gagal menyimpan ke Keep.", "error");
      return;
    }

    setKeepProgress({ active: false, current: 0, total: 0 });
    setShowKeepModal(false);
    setSelectedKeepRows({});

    const failures = getBulkFailures(response.results, payloads, (payload) => payload?.reservation_code || payload?.row_id || "-");
    // DUPLICATE = transaksi sudah ada di Keep (juga kalau request sebelumnya ternyata sudah tersimpan).
    const duplicates = failures.filter((failure) => failure.result.status === "duplicate");
    const realFailures = failures.filter((failure) => failure.result.status !== "duplicate");

    if (failures.length === 0) {
      Swal.fire("Pemberitahuan", `${response.okCount} transaksi berhasil di-keep!`, "success");
      return;
    }
    Swal.fire({
      icon: realFailures.length ? "warning" : "info",
      title: `${response.okCount} tersimpan${duplicates.length ? `, ${duplicates.length} sudah ada di Keep` : ""}${realFailures.length ? `, ${realFailures.length} gagal` : ""}`,
      html: buildBulkFailureHtml(failures),
    });
  };

  const [isCreatingMaster, setIsCreatingMaster] = useState(false);
  const handleCreateQuickMasterKeep = async () => {
    if (!newMasterName.trim()) {
      Swal.fire("Pemberitahuan", "Nama koordinator tidak boleh kosong", "warning");
      return;
    }
    setIsCreatingMaster(true);
    try {
      const res = await postRequest("/master-keep", { name: newMasterName });
      if (res.success && res.data) {
        setMasterKeepList(prev => [...prev, res.data]);
        setKeepForm(prev => ({ ...prev, master_keep_id: res.data.id }));
        setShowQuickCreate(false);
        setNewMasterName("");
      } else {
        Swal.fire("Pemberitahuan", res.message || "Gagal membuat Master Keep baru", "error");
      }
    } catch (error) {
      Swal.fire("Pemberitahuan", "Terjadi kesalahan saat memanggil API", "warning");
    } finally {
      setIsCreatingMaster(false);
    }
  };

  const handleSubmitPayment = (e) => {
    e.preventDefault();
    if (!paymentForm.payment_method_id || !paymentForm.amount) return;

    const payload = {
      reservation_income_id: paymentForm.reservation_income_id,
      payment_method_id: paymentForm.payment_method_id,
      amount: Number(paymentForm.amount)
    };

    if (paymentForm.id) {
      updateReservationPayment.mutate({ id: paymentForm.id, payload }, {
        onSuccess: () => {
          setShowPaymentModal(false);
        },
        onError: (err) => {
          Swal.fire("Pemberitahuan", err.message || "Failed to edit payment", "error");
        }
      });
    } else {
      createReservationPayment.mutate(payload, {
        onSuccess: () => {
          setShowPaymentModal(false);
        },
        onError: (err) => {
          Swal.fire("Pemberitahuan", err.message || "Failed to create payment", "error");
        }
      });
    }
  };

  // Pre-calculate totals for footer rows
  const totalNetAgentKiri = total?.net_agent || 0;
  
  const { totalPjRp, totalBmmmRp, totalNetAgentKanan } = tableData.reduce((acc, row) => {
    const rowId = row.id;
    const netKuber = getManualNetKuberValue(row, editedRows);
    const others = editedRows[rowId]?.others ?? (Number(row.others) || 0);
    
    let totalMotor = 0;
    row.activities?.forEach(act => {
      if (act.activity?.includes("ATV")) totalMotor += act.qty;
    });
    
    const defaultPjQty = getStoredPjQty(row, totalMotor);
    const defaultBmmmQty = getStoredBmmmQty(row, totalMotor);
    
    const pjQty = editedRows[rowId]?.pj ?? defaultPjQty;
    const bmmmQty = editedRows[rowId]?.bmmm ?? defaultBmmmQty;
    
    const valPJ = pjQty * 25000;
    const valBMMM = bmmmQty * 10000;
    
    // Wait, the user asked for "total net agent kanan" which is the right-most column calculation.
    // The right-most column is `calculatedNetAgent` from line 464 which is netKuber + others + valPJ + valBMMM
    const calculatedNetAgent = netKuber + others + valPJ + valBMMM;
    
    acc.totalPjRp += valPJ;
    acc.totalBmmmRp += valBMMM;
    acc.totalNetAgentKanan += calculatedNetAgent;
    return acc;
  }, { totalPjRp: 0, totalBmmmRp: 0, totalNetAgentKanan: 0 });

  // Nilai harian yang dikirim manual ke Menu Pembukuan (sama dengan angka di baris TOTAL / ATV VOUCHER).
  const bookkeepingSyncValues = useMemo(() => [
    {
      section: "nam",
      name: "NAM",
      label: "NAM",
      hint: "ATV VOUCHER = Total Net Agent kiri − Total Net Agent kanan",
      amount: totalNetAgentKiri - totalNetAgentKanan,
    },
    {
      section: "others",
      name: "OTHERS",
      label: "Others",
      hint: "Total kolom OTHERS",
      amount: Number(total?.others) || 0,
    },
    {
      section: "josh",
      name: "FEE JOSH",
      label: "Josh",
      hint: "Total PJ (Rp) + Total BM/MM (Rp)",
      amount: totalPjRp + totalBmmmRp,
    },
  ], [totalNetAgentKiri, totalNetAgentKanan, total?.others, totalPjRp, totalBmmmRp]);

  const summaryNetKuber = Number(accountingSummary?.data?.summary?.total_net_kuber || 0);
  const localProfitRafting = getExtraSectionTotal('profit', 'col2');
  const localTipping = getExtraSectionTotal('lainLain', 'col2');
  const localBiayaTransport = getExtraSectionTotal('transport', 'col2');
  const localBiayaBiaya = getExtraSectionTotal('biaya', 'col2');
  const localCashOnHandDeduction = getExtraSectionTotal('cashOnHand', 'col2');
  const localTotalNetAgentEDC = (accountingByPayment?.data || []).reduce((acc, curr) => acc + Number(curr.total_amount || curr.total_net_agent || curr.amount || 0), 0);
  const localNetRevenue = summaryNetKuber - localBiayaBiaya;
  const localCashOnHand = summaryNetKuber + localProfitRafting + localTipping - localBiayaTransport - localBiayaBiaya - localTotalNetAgentEDC - localCashOnHandDeduction;
  const resumeRevenueAtv = getResumeValue(["revenue_atv", "total_revenue_atv", "total_net_kuber"], summaryNetKuber);
  const resumeTransport = getResumeValue(["transport_revenue", "transport", "biaya_transport"], localBiayaTransport);
  const resumeProfitLain = getResumeValue(["profit_lain_activities", "profit_lain_aktivitas", "profit_lain_aktivitas_total"], localProfitRafting);
  const resumeTipping = getResumeValue(["tipping", "tiping", "lain_lain"], localTipping);
  const resumeOtherRevenue = getResumeValue(["other_revenue", "other_income"], 0);
  const resumeBiayaBiaya = getResumeValue(["biaya_biaya", "total_biaya", "expenses"], localBiayaBiaya);
  const resumeNetRevenue = getResumeValue(["net_revenue", "total_net_revenue"], localNetRevenue);
  const resumeCashOnHand = getResumeValue(["cash_on_hand", "total_cash_on_hand"], localCashOnHand);

  const handleExportExcel = () => {
    setIsExporting(true);
    
    setTimeout(() => {
      try {
        let tableContent = "";

        // ---- MAIN TABLE (ATV) ----
        tableContent += `
          <tr>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">NO</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">TGL</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: left;">TRANSAKSI</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">PAYMENT BILL</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">NET AGENT</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">SUSUK AGENT</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">NET KUBER</th>
            <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">TOTAL</th>
            <th style="background-color: #e6b8af; border: 1px solid #000; text-align: center;">OTHERS</th>
            <th style="background-color: #ffff00; border: 1px solid #000; text-align: center;">PJ</th>
            <th style="background-color: #d5a6bd; border: 1px solid #000; text-align: center;">BM<br/>MM</th>
            <th style="background-color: #ffff00; border: 1px solid #000; text-align: center;">PJ</th>
            <th style="background-color: #cc4125; color: #fff; border: 1px solid #000; text-align: center;">BM<br/>MM</th>
            <th style="background-color: #e8e8ba; border: 1px solid #000; text-align: center;">NET<br/>AGENT</th>
          </tr>
          <tr>
            <th colspan="14" style="background-color: #000; color: #fff; text-align: left; border: 1px solid #000; padding-left: 10px;">ATV</th>
          </tr>
        `;

        if (tableData.length > 0) {
          tableData.forEach((row, index) => {
            let singleQty = 0; let tandemQty = 0;
            row.activities?.forEach(act => {
              if (act.activity === "ATV Single") singleQty += act.qty;
              if (act.activity === "ATV Tandem") tandemQty += act.qty;
            });
            let qtyStr = [];
            if (singleQty > 0) qtyStr.push(`${singleQty} SINGLE`);
            if (tandemQty > 0) qtyStr.push(`${tandemQty} TANDEM`);
            const typeLabel = getAccountingTypeLabel(row);
            const typeStr = typeLabel ? `/${typeLabel}` : '';
            const guestTransport = row.guest_transport ? `/${row.guest_transport.toUpperCase()}` : '';
            const customerName = row.guest_name ? `/${row.guest_name.toUpperCase()}` : '';
            const hotel = row.hotel ? `/${row.hotel.toUpperCase()}` : '';
            const agentName = row.agent_name ? `/${row.agent_name.toUpperCase()}` : '';
            const transaksiText = `ATV ${qtyStr.join(',')}${typeStr}${guestTransport}${customerName}${hotel}${agentName}`;
            
            const rowId = row.id;
            const paymentBill = editedRows[rowId]?.paymentBill ?? (Number(row.payment_bill) || 0);
            const netAgent = editedRows[rowId]?.netAgent ?? (Number(row.net_agent) || 0);
            const susukAgent = editedRows[rowId]?.susukAgent ?? (Number(row.susuk_agent) || 0);
            const others = editedRows[rowId]?.others ?? (Number(row.others) || 0);
            const totalMotor = singleQty + tandemQty;
            const typeVal = (row.type || "").toLowerCase();
            const isPJ = typeVal === 'pj' || typeVal === 'josh' || typeVal === 'j';
            const isBMMM = typeVal === 'bm' || typeVal === 'mm';
            const defaultPjQty = isPJ ? totalMotor : 0;
            const defaultBMMMQty = isBMMM ? totalMotor : 0;
            const pjQty = editedRows[rowId]?.pj ?? defaultPjQty;
            const bmmmQty = editedRows[rowId]?.bmmm ?? defaultBMMMQty;
            const netKuber = getManualNetKuberValue(row, editedRows);
            const valPJ = pjQty * 25000;
            const valBMMM = bmmmQty * 10000;
            const calculatedNetAgent = netKuber + others + valPJ + valBMMM;

            tableContent += `
              <tr>
                <td style="border: 1px solid #000; text-align: center; font-weight: bold;">${index + 1}</td>
                <td style="border: 1px solid #000; text-align: center; font-weight: bold;">${shortDate}</td>
                <td style="border: 1px solid #000;">${transaksiText}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(paymentBill)}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(netAgent)}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(susukAgent)}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(netKuber)}</td>
                <td style="border: 1px solid #000; text-align: center; font-weight: bold;">${totalMotor}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(others)}</td>
                <td style="border: 1px solid #000; text-align: center; font-weight: bold;">${pjQty}</td>
                <td style="border: 1px solid #000; text-align: center; font-weight: bold;">${bmmmQty}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(valPJ)}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(valBMMM)}</td>
                <td style="border: 1px solid #000; text-align: right; background-color: #f8f9fa;">${formatPrice(calculatedNetAgent)}</td>
              </tr>
            `;
          });
        }

        // TOTAL MAIN TABLE
        const totalNetKuber = tableData.reduce((acc, row) => acc + (getManualNetKuberValue(row, editedRows)), 0);
        const totalTotalMotor = tableData.reduce((acc, row) => {
          let singleQty = 0; let tandemQty = 0;
          row.activities?.forEach(act => {
            if (act.activity === "ATV Single") singleQty += act.qty;
            if (act.activity === "ATV Tandem") tandemQty += act.qty;
          });
          return acc + singleQty + tandemQty;
        }, 0);
        const totalOthers = tableData.reduce((acc, row) => acc + (editedRows[row.id]?.others ?? (Number(row.others) || 0)), 0);
        const totalPjQty = tableData.reduce((acc, row) => {
          const isPJ = (row.type || "").toLowerCase() === 'pj' || (row.type || "").toLowerCase() === 'josh' || (row.type || "").toLowerCase() === 'j';
          let totalMotor = 0; row.activities?.forEach(act => { if (act.activity?.includes("ATV")) totalMotor += act.qty; });
          return acc + (editedRows[row.id]?.pj ?? (isPJ ? totalMotor : 0));
        }, 0);
        const totalBmmmQty = tableData.reduce((acc, row) => {
          const isBMMM = (row.type || "").toLowerCase() === 'bm' || (row.type || "").toLowerCase() === 'mm';
          let totalMotor = 0; row.activities?.forEach(act => { if (act.activity?.includes("ATV")) totalMotor += act.qty; });
          return acc + (editedRows[row.id]?.bmmm ?? (isBMMM ? totalMotor : 0));
        }, 0);

        tableContent += `
          <tr style="background-color: #c6e0b4; font-weight: bold;">
            <td colspan="3" style="border: 1px solid #000; text-align: center;">TOTAL</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(total?.payment_bill || 0)}</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(total?.net_agent || 0)}</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(total?.susuk_agent || 0)}</td>
            <td style="border: 1px solid #000; text-align: right; background-color: #00bfff; color: #000;">${formatPrice(totalNetKuber)}</td>
            <td style="border: 1px solid #000; text-align: center;">${totalTotalMotor}</td>
            <td style="border: 1px solid #000; text-align: right; background-color: #ffd966; color: #000;">${formatPrice(totalOthers)}</td>
            <td style="border: 1px solid #000; text-align: center; background-color: #ffe699; color: #000;">${totalPjQty}</td>
            <td style="border: 1px solid #000; text-align: center; background-color: #ffe699; color: #000;">${totalBmmmQty}</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(totalPjRp)}</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(totalBmmmRp)}</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(totalNetAgentKanan)}</td>
          </tr>
          <tr><td colspan="14"></td></tr>
        `;

        // ---- EXTRA SECTIONS (BIAYA) ----
        tableContent += `
          <tr>
            <th colspan="14" style="background-color: #000; color: #fff; font-style: italic; text-align: left; padding-left: 10px; border: 1px solid #000;">BIAYA - BIAYA</th>
          </tr>
        `;
        extraSections.biaya.forEach((item) => {
          tableContent += `
            <tr>
              <td style="border: 1px solid #000;"></td>
              <td style="border: 1px solid #000; text-align: center;">${formattedDateTitle}</td>
              <td style="border: 1px solid #000;">${item.name || ''}</td>
              <td style="border: 1px solid #000; text-align: right;">${item.col1 ? formatPrice(Number(String(item.col1).replace(/[^0-9.-]+/g, '')) || 0) : ''}</td>
              <td style="border: 1px solid #000; text-align: right;">${item.col2 ? formatPrice(Number(String(item.col2).replace(/[^0-9.-]+/g, '')) || 0) : ''}</td>
              <td style="border: 1px solid #000; text-align: right;">${item.col3 ? formatPrice(Number(String(item.col3).replace(/[^0-9.-]+/g, '')) || 0) : ''}</td>
              <td style="border: 1px solid #000; text-align: right;">${item.col4 ? formatPrice(Number(String(item.col4).replace(/[^0-9.-]+/g, '')) || 0) : ''}</td>
              <td style="border: 1px solid #000; text-align: right;">${item.col5 ? formatPrice(Number(String(item.col5).replace(/[^0-9.-]+/g, '')) || 0) : ''}</td>
              <td colspan="6" style="border: 1px solid #000;"></td>
            </tr>
          `;
        });
        
        tableContent += `
          <tr style="background-color: #99cc00; font-weight: bold;">
            <td colspan="4" style="border: 1px solid #000; text-align: center;">TOTAL</td>
            <td style="border: 1px solid #000; text-align: right;">${formatPrice(getExtraSectionTotal('biaya', 'col2'))}</td>
            <td style="border: 1px solid #000;"></td>
            <td style="border: 1px solid #000;"></td>
            <td style="border: 1px solid #000;"></td>
            <td colspan="6" style="border: 1px solid #000;"></td>
          </tr>
          <tr><td colspan="14"></td></tr>
        `;

        // ---- RESUME TRANSAKSI (EDC) ----
        const edcGroups = {};
        tableData.forEach(reservation => {
          const paymentItems = reservation.reservation_income?.payment_items || [];
          paymentItems.forEach(item => {
            const pmName = item.payment_method?.name || "UNKNOWN EDC";
            if (!edcGroups[pmName]) edcGroups[pmName] = [];
            edcGroups[pmName].push({ reservation, paymentItem: item });
          });
        });

        tableContent += `
          <tr>
            <th colspan="6" style="background-color: #000; color: #fff; text-align: left; padding-left: 10px; border: 1px solid #000; font-size: 16px;">RESUME TRANSAKSI</th>
          </tr>
        `;

        Object.entries(edcGroups).forEach(([pmName, items]) => {
          const sortedItems = sortByCreatedAsc(items.map((item) => ({
            ...item,
            created_at: item.paymentItem?.created_at || item.reservation?.created_at,
          })));
          let edcTotalNetAgent = 0;
          let edcTotalCharge = 0;
          
          tableContent += `
            <tr>
              <th colspan="6" style="background-color: #343a40; color: #fff; text-align: left; padding-left: 10px; font-style: italic; border: 1px solid #000;">
                ${pmName}
              </th>
            </tr>
            <tr>
              <th style="background-color: #f8f9fa; border: 1px solid #000;">TGL</th>
              <th style="background-color: #f8f9fa; border: 1px solid #000;">TRANSAKSI</th>
              <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">PAYMENT</th>
              <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">%</th>
              <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">TAX</th>
              <th style="background-color: #f8f9fa; border: 1px solid #000; text-align: center;">TOTAL</th>
            </tr>
          `;
          
          sortedItems.forEach(row => {
            const { reservation, paymentItem } = row;
            const paymentId = paymentItem.id;
            const rawAmount = editedPayments[paymentId] !== undefined ? editedPayments[paymentId] : (Number(paymentItem.amount) || 0);
            const taxRate = editedPaymentTaxRates[paymentId] ?? getPaymentTaxRate(paymentItem);
            const charge = getPaymentTaxAmount(paymentItem, rawAmount, editedPaymentTaxes, editedPaymentTaxRates);
            
            edcTotalNetAgent += rawAmount;
            edcTotalCharge += charge;
            
            let singleQty = 0; let tandemQty = 0;
            reservation.activities?.forEach(act => {
              if (act.activity === "ATV Single") singleQty += act.qty;
              if (act.activity === "ATV Tandem") tandemQty += act.qty;
            });
            let qtyStr = [];
            if (singleQty > 0) qtyStr.push(`${singleQty} SINGLE`);
            if (tandemQty > 0) qtyStr.push(`${tandemQty} TANDEM`);
            const typeLabel = getAccountingTypeLabel(reservation);
            const typeStr = typeLabel ? `/${typeLabel}` : '';
            const guestTransport = reservation.guest_transport ? `/${reservation.guest_transport.toUpperCase()}` : '';
            const hotel = reservation.hotel ? `/${reservation.hotel.toUpperCase()}` : '';
            const transaksiText = `ATV ${qtyStr.join(',')}${typeStr}${guestTransport}/${reservation.guest_name?.toUpperCase()}${hotel}/${reservation.agent_name?.toUpperCase()}`;
            
            const resDate = reservation.reservation_date ? new Date(reservation.reservation_date) : new Date();
            const resShortDate = resDate.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "2-digit" }).toUpperCase();
            
            tableContent += `
              <tr>
                <td style="border: 1px solid #000; text-align: center;">${resShortDate}</td>
                <td style="border: 1px solid #000;">${transaksiText}</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(rawAmount)}</td>
                <td style="border: 1px solid #000; text-align: right;">${taxRate}%</td>
                <td style="border: 1px solid #000; text-align: right;">${formatPrice(charge)}</td>
                <td style="border: 1px solid #000; text-align: right; font-weight: bold;">${formatPrice(rawAmount + charge)}</td>
              </tr>
            `;
          });
          
          tableContent += `
            <tr style="background-color: #c6e0b4; font-weight: bold;">
              <td colspan="2" style="border: 1px solid #000; text-align: center;">TOTAL</td>
              <td style="border: 1px solid #000; text-align: right;">${formatPrice(edcTotalNetAgent)}</td>
              <td style="border: 1px solid #000;"></td>
              <td style="border: 1px solid #000; text-align: right;">${formatPrice(edcTotalCharge)}</td>
              <td style="border: 1px solid #000; text-align: right;">${formatPrice(edcTotalNetAgent + edcTotalCharge)}</td>
            </tr>
            <tr><td colspan="6"></td></tr>
          `;
        });

        // ---- CONSTRUCT HTML AND EXPORT ----
        const fullHtml = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
            <head>
              <meta charset="UTF-8">
              <!--[if gte mso 9]>
              <xml>
                <x:ExcelWorkbook>
                  <x:ExcelWorksheets>
                    <x:ExcelWorksheet>
                      <x:Name>AccountingV2</x:Name>
                      <x:WorksheetOptions>
                        <x:DisplayGridlines/>
                      </x:WorksheetOptions>
                    </x:ExcelWorksheet>
                  </x:ExcelWorksheets>
                </x:ExcelWorkbook>
              </xml>
              <![endif]-->
              <style>
                table { border-collapse: collapse; font-family: Calibri, sans-serif; }
                td, th { vertical-align: middle; }
              </style>
            </head>
            <body>
              <table>
                <tbody>
                  ${tableContent}
                </tbody>
              </table>
            </body>
          </html>
        `;
        const blob = new Blob([fullHtml], { type: 'application/vnd.ms-excel' });
        const fileName = `Accounting_${selectedDate}.xls`;
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Excel export error:", error);
        Swal.fire("Error", "Terjadi kesalahan saat mengekspor Excel", "error");
      } finally {
        setIsExporting(false);
      }
    }, 100);
  };


  return (
    <LayoutDashboard>
      <div className="container-fluid py-3 pb-5 mb-5 accounting-v2-container">
        {isQuickSearchOpen && (
          <div className={`accounting-quick-search accounting-quick-search-${quickSearchSide}`}>
            <div className="accounting-quick-search-header">
              <span className="fw-bold">Cari data</span>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  className={`accounting-quick-search-icon ${quickSearchSide === "left" ? "active" : ""}`}
                  title="Pindah ke kiri atas"
                  onClick={() => setQuickSearchSide("left")}
                >
                  Kiri
                </button>
                <button
                  type="button"
                  className={`accounting-quick-search-icon ${quickSearchSide === "right" ? "active" : ""}`}
                  title="Pindah ke kanan atas"
                  onClick={() => setQuickSearchSide("right")}
                >
                  Kanan
                </button>
                <button
                  type="button"
                  className="accounting-quick-search-close"
                  title="Tutup"
                  onClick={() => setIsQuickSearchOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="accounting-quick-search-body">
              <Form.Control
                ref={quickSearchInputRef}
                size="sm"
                type="text"
                placeholder="Cari transaksi, guest, hotel, agent..."
                value={quickSearchQuery}
                onChange={(event) => setQuickSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    moveQuickSearchMatch(event.shiftKey ? -1 : 1);
                  }
                }}
              />
              <div className="accounting-quick-search-actions">
                <span>
                  {quickSearchQuery.trim()
                    ? `${quickSearchMatches.length ? quickSearchIndex + 1 : 0}/${quickSearchMatches.length}`
                    : "Ctrl+F"}
                </span>
                <button type="button" onClick={() => moveQuickSearchMatch(-1)} disabled={!quickSearchMatches.length}>
                  ↑
                </button>
                <button type="button" onClick={() => moveQuickSearchMatch(1)} disabled={!quickSearchMatches.length}>
                  ↓
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="row align-items-center mb-2 g-2 accounting-v2-top-toolbar">
          {/* Left: Search Hint */}
          <div className="col-12 col-lg-3 d-flex align-items-center justify-content-center justify-content-lg-start accounting-v2-toolbar-left">
            <button
              type="button"
              className="badge bg-light text-muted border px-3 py-2 shadow-sm d-flex align-items-center accounting-search-hint"
              style={{ fontSize: '13px' }}
              onClick={() => {
                setIsQuickSearchOpen(true);
                setTimeout(() => quickSearchInputRef.current?.focus(), 0);
              }}
            >
              <i className="fe fe-search me-2 text-primary fs-6"></i> 
              <span className="fw-normal">Gunakan <kbd className="ms-1 me-1 px-2">Ctrl</kbd> + <kbd className="ms-1 me-1 px-2">F</kbd> untuk mencari</span>
            </button>
          </div>
          
          {/* Center: Title */}
          <div className="col-12 col-lg-4 text-center accounting-v2-toolbar-title">
            <h3 className="m-0 fw-bold" style={{ fontSize: '18px' }}>TRANSAKSI HARIAN KUBER BALI ADVENTURE</h3>
            <h4 className="m-0 fw-bold" style={{ fontSize: '16px' }}>TANGGAL : {formattedDateTitle}</h4>
          </div>

          {/* Right: Auto Save Status Indicator & Export Button */}
          <div className="col-12 col-lg-5 d-flex justify-content-center justify-content-lg-end gap-2 accounting-v2-toolbar-actions">
            <div
              className="d-flex align-items-center px-3 py-1 rounded shadow-sm border"
              title={isOnline ? "Koneksi aktif, data bisa tersimpan ke server." : "Koneksi offline, perubahan berisiko belum tersimpan ke server."}
              style={{
                backgroundColor: isOnline ? '#e7f5ff' : '#f8d7da',
                color: isOnline ? '#0b5ed7' : '#842029',
                transition: 'all 0.3s ease-in-out',
                height: '34px',
                minWidth: '105px',
                justifyContent: 'center'
              }}
            >
              <i className={`fe ${isOnline ? 'fe-wifi' : 'fe-wifi-off'} me-2`}></i>
              <span className="small fw-bold">{isOnline ? "Online" : "Offline"}</span>
            </div>
            <div 
              className="d-flex align-items-center px-3 py-1 rounded shadow-sm border" 
              style={{ 
                backgroundColor: !isOnline || saveStatus === 'error' ? '#f8d7da' : saveStatus === 'saving' ? '#fff3cd' : saveStatus === 'success' ? '#d1e7dd' : '#f8f9fa',
                color: !isOnline || saveStatus === 'error' ? '#842029' : saveStatus === 'saving' ? '#856404' : saveStatus === 'success' ? '#0f5132' : '#6c757d',
                transition: 'all 0.3s ease-in-out',
                height: '34px',
                minWidth: '170px',
                justifyContent: 'center'
              }}
            >
              {saveStatus === 'saving' ? (
                <><Spinner animation="border" size="sm" className="me-2" /> <span className="small fw-bold">Menyimpan data...</span></>
              ) : saveStatus === 'success' ? (
                <><i className="fe fe-check-circle me-2"></i> <span className="small fw-bold">Perubahan tersimpan</span></>
              ) : !isOnline ? (
                <><i className="fe fe-alert-triangle me-2"></i> <span className="small fw-bold">Offline, belum aman</span></>
              ) : saveStatus === 'error' ? (
                <><i className="fe fe-x-circle me-2"></i> <span className="small fw-bold">Gagal menyimpan</span></>
              ) : (
                <><i className="fe fe-cloud me-2"></i> <span className="small fw-bold">Auto-save Aktif</span></>
              )}
            </div>
            <BulkReservationDevTool
              selectedDate={selectedDate}
              paymentMethods={allPaymentMethods}
              onSuccess={() => {
                refetch?.();
                accountingSummary?.refetch?.();
                accountingByPayment?.refetch?.();
                accountingResume?.refetch?.();
              }}
            />
            <Button 
              variant="success" 
              size="sm" 
              onClick={handleExportExcel} 
              className="d-flex align-items-center shadow-sm" 
              style={{ height: '34px' }}
              disabled={isExporting}
            >
              {isExporting ? (
                <><Spinner animation="border" size="sm" className="me-2" /> <span className="small fw-bold">Memproses...</span></>
              ) : (
                <><i className="fe fe-download me-2"></i> Export Excel</>
              )}
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={handleOpenSelectedKeepModal}
              className="d-flex align-items-center shadow-sm"
              style={{ height: '34px', whiteSpace: 'nowrap' }}
              disabled={selectedKeepRowsList.length === 0 || keepTransaction.isPending}
            >
              <i className="fe fe-file-text me-2"></i>
              Keep Terpilih ({selectedKeepRowsList.length})
            </Button>
            <Button
              variant="info"
              size="sm"
              onClick={() => setTrfModalRows(tableData.filter((row) => selectedKeepRows[row.id]))}
              className="d-flex align-items-center shadow-sm text-white"
              style={{ height: '34px', whiteSpace: 'nowrap' }}
              disabled={selectedKeepRowsList.length === 0}
              title="Tambahkan transaksi terpilih ke Trf BCA / Trf Mandiri / Trf BRI di Menu Pembukuan"
            >
              <i className="fe fe-repeat me-2"></i>
              Tambah ke Trf ({selectedKeepRowsList.length})
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowBookkeepingSync(true)}
              className="d-flex align-items-center shadow-sm"
              style={{ height: '34px', whiteSpace: 'nowrap' }}
              disabled={tableData.length === 0}
              title="Kirim NAM, Others, dan Fee Josh tanggal ini ke Menu Pembukuan"
            >
              <i className="fe fe-send me-2"></i>
              Kirim ke Pembukuan
            </Button>
            {selectedKeepRowsList.length > 0 && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setSelectedKeepRows({})}
                className="d-flex align-items-center shadow-sm"
                style={{ height: '34px', whiteSpace: 'nowrap' }}
                disabled={keepTransaction.isPending}
              >
                <i className="fe fe-x-circle me-2"></i>
                Unselect All
              </Button>
            )}
          </div>
        </div>

        {/* Main Table */}
        <div className="accounting-v2-main-table-wrap">
        <table className="accounting-v2-table accounting-v2-main-table" style={{ width: "100%", minWidth: "1200px" }}>
          <thead>
            <tr>
              <th className="bg-light text-center" style={{ width: '48px' }}>NO</th>
              <th className="bg-light" title="Urutan mengikuti sort_order dari API">
                TGL
              </th>
              <th className="bg-light" title="Urutan mengikuti sort_order dari API">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-transaksi">
                      Format: ATV / GUEST_TRANSPORT / GUEST_NAME / HOTEL / AGENT_NAME
                    </Tooltip>
                  }
                >
                  <span>TRANSAKSI <i className="fe fe-info text-black ms-1" style={{ fontSize: '10px' }}></i></span>
                </OverlayTrigger>
              </th>
              <th className="bg-light text-center">PAYMENT BILL</th>
              <th className="bg-light text-center">NET AGENT</th>
              <th className="bg-light text-center">SUSUK AGENT</th>
              <th className="bg-light text-center">NET KUBER</th>
              <th className="bg-light text-center">TOTAL</th>
              <OverlayTrigger placement="top" overlay={<Tooltip>Perhitungan: Total Motor × 50.000</Tooltip>}>
                <th className="text-center text-black" style={{ cursor: "help", backgroundColor: '#e6b8af' }}>OTHERS</th>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Kuantitas motor untuk tipe PJ / Josh</Tooltip>}>
                <th className="text-center text-black" style={{ cursor: "help", backgroundColor: '#ffff00' }}>PJ</th>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Kuantitas motor untuk tipe BM / MM</Tooltip>}>
                <th className="text-center text-black" style={{ cursor: "help", backgroundColor: '#d5a6bd' }}>BM<br/>MM</th>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Potongan: Total Motor × 25.000 (Khusus tipe PJ/Josh)</Tooltip>}>
                <th className="text-center text-black" style={{ cursor: "help", backgroundColor: '#ffff00' }}>PJ</th>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Potongan: Total Motor × 10.000 (Khusus tipe BM/MM)</Tooltip>}>
                <th className="text-center" style={{ cursor: "help", backgroundColor: '#cc4125', color: '#fff' }}>BM<br/>MM</th>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={<Tooltip>Perhitungan: Net Kuber + Others + PJ + BM/MM</Tooltip>}>
                <th className="text-center text-black" style={{ cursor: "help", backgroundColor: '#e8e8ba' }}>NET<br/>AGENT</th>
              </OverlayTrigger>
            </tr>
            <tr>
              <th className="table-header-black" colSpan={14}>ATV</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, index) => {
                // Determine quantities
                let singleQty = 0;
                let tandemQty = 0;
                row.activities?.forEach(act => {
                  if (act.activity === "ATV Single") singleQty += act.qty;
                  if (act.activity === "ATV Tandem") tandemQty += act.qty;
                });
                
                const transaksiText = getAccountingTransactionText(row);
                
                const rowId = row.id;
                const rowEdited = isAccountingRowEdited(row);
                
                const paymentBill = editedRows[rowId]?.paymentBill ?? (Number(row.payment_bill) || 0);
                const netAgent = editedRows[rowId]?.netAgent ?? (Number(row.net_agent) || 0);
                const susukAgent = editedRows[rowId]?.susukAgent ?? (Number(row.susuk_agent) || 0);
                const others = editedRows[rowId]?.others ?? (Number(row.others) || 0);
                const totalMotor = singleQty + tandemQty;
                
                // Use edited state if available, else default
                const defaultPjQty = getStoredPjQty(row, totalMotor);
                const defaultBmmmQty = getStoredBmmmQty(row, totalMotor);
                                const pjQty = editedRows[rowId]?.pj ?? defaultPjQty;
                const bmmmQty = editedRows[rowId]?.bmmm ?? defaultBmmmQty;
                const netKuber = getManualNetKuberValue(row, editedRows);
                
                // Calculate values based on (edited or default) qty
                const valPJ = pjQty * 25000;
                const valBMMM = bmmmQty * 10000;
                
                // Calculate far-right Net Agent
                const calculatedNetAgent = netKuber + others + valPJ + valBMMM;
                const isSelectedForKeep = Boolean(selectedKeepRows[rowId]);
                const isRowSaving = Boolean(savingRows[rowId]);
                const rowFeedback = isRowSaving ? null : rowSaveFeedback[rowId];
                
                return (
                  <tr
                    key={index}
                    onDragOver={(event) => {
                      if (!draggedRowId) return;
                      event.preventDefault();
                      if (dragOverRowId !== rowId) setDragOverRowId(rowId);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      handleTransactionDrop(rowId);
                    }}
                    ref={(element) => {
                      if (element) rowRefs.current[rowId] = element;
                      else delete rowRefs.current[rowId];
                    }}
                    className={[
                      (savedRows[rowId] || rowEdited || Boolean(editedRows[rowId])) ? "accounting-row-edited" : "",
                      isSelectedForKeep ? "accounting-row-keep-selected" : "",
                      activeQuickSearchRowId === rowId ? "accounting-row-search-active" : "",
                      draggedRowId === rowId ? "accounting-row-dragging" : "",
                      isRowSaving ? "accounting-row-saving" : "",
                      rowFeedback === 'success' ? "accounting-row-save-success" : "",
                      rowFeedback === 'error' ? "accounting-row-save-error" : "",
                      draggedRowId && dragOverRowId === rowId && draggedRowId !== rowId ? "accounting-row-drag-over" : "",
                    ].filter(Boolean).join(" ")}
                  >
                    <td className="text-center fw-bold accounting-number-cell">
                      <span
                        className="accounting-drag-handle"
                        draggable
                        title="Seret nomor untuk mengubah urutan"
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", String(rowId));
                          const rowElement = rowRefs.current[rowId];
                          if (rowElement) event.dataTransfer.setDragImage(rowElement, 20, 10);
                          setDraggedRowId(rowId);
                        }}
                        onDragEnd={() => {
                          setDraggedRowId(null);
                          setDragOverRowId(null);
                        }}
                      >
                        {isSelectedForKeep ? "✓" : index + 1}
                      </span>
                      {isRowSaving && (
                        <span className="accounting-row-status accounting-row-status-saving" title="Menyimpan..." role="status" aria-label="Menyimpan">
                          <Spinner animation="border" size="sm" />
                        </span>
                      )}
                      {rowFeedback === 'success' && (
                        <span className="accounting-row-status accounting-row-status-success" title="Tersimpan" role="status" aria-label="Tersimpan">✓</span>
                      )}
                      {rowFeedback === 'error' && (
                        <span className="accounting-row-status accounting-row-status-error" title="Gagal disimpan. Edit ulang sel untuk mencoba lagi." role="alert" aria-label="Gagal disimpan">!</span>
                      )}
                      {rowEdited && (
                        <span className="accounting-edited-actions">
                          <span className="accounting-edited-check ms-1" title="Data sudah dikerjakan">✓</span>
                          {resettingRows[rowId] ? (
                            <Spinner animation="border" size="sm" className="accounting-reset-edited-spinner" title="Sedang menghapus last updated" />
                          ) : (
                            <button
                              type="button"
                              className="accounting-reset-edited-btn"
                              title="Hapus last updated"
                              onClick={() => handleResetLastUpdated(row)}
                            >
                              ×
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td
                      className="text-center fw-bold accounting-date-cell"
                      onMouseDown={(event) => handleKeepSelectionStart(event, index, row)}
                      onMouseEnter={() => handleKeepSelectionEnter(index)}
                      title="Tap atau drag tanggal untuk memilih data Keep"
                      style={{
                        cursor: "crosshair",
                        userSelect: "none",
                      }}
                    >
                      <span className="accounting-row-date">{shortDate}</span>
                    </td>
                    <td className="text-end p-0">
                      <Form.Control 
                        readOnly 
                        type="text" 
                        size="sm" 
                        className="fw-bold border-0 bg-transparent h-100 text-primary text-decoration-underline" 
                        style={{ 
                          width: `${Math.max(30, transaksiText.length + 5)}ch`, 
                          minWidth: '200px',
                          maxWidth: '600px',
                          boxShadow: 'none', 
                          cursor: 'pointer'
                        }}
                        value={transaksiText}
                        data-row={index} 
                        data-col="transaksi"
                        onClick={() => handleOpenPaymentModal(row, transaksiText)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleOpenPaymentModal(row, transaksiText);
                          } else {
                            handleKeyDown(e, index, 'transaksi', rowId, row);
                          }
                        }}
                        title="Tekan Enter / Klik untuk membuat Payment"
                      />
                      {row.track === "B" && <span className="trip-b-label">TRIP B</span>}
                    </td>
                    <td className="text-end p-0">
                      <Form.Control readOnly={activeEditCell !== `${rowId}-paymentBill`} onClick={() => activateEditCell(rowId, 'paymentBill')} onDoubleClick={() => activateEditCell(rowId, 'paymentBill')} type="text" size="sm" className={`text-end border-0 bg-transparent h-100 ${activeEditCell === `${rowId}-paymentBill` ? "bg-white shadow-sm" : ""}`} style={{ minWidth: '80px', boxShadow: 'none' }}
                        value={activeEditCell === `${rowId}-paymentBill` ? (editedRows[rowId]?.paymentBillFormula ?? paymentBill) : paymentBill.toLocaleString('id-ID')} data-row={index} data-col="paymentBill"
                        data-cell={`${rowId}-paymentBill`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-paymentBill` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-paymentBill` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'paymentBill', rowId, row)}
                        onChange={(e) => { const formula = e.target.value; const result = evaluateAccountingFormula(formula); setEditedRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], paymentBill: result ?? 0, paymentBillFormula: formula } })) }}
                        onBlur={(e) => { if (activeEditCell === `${rowId}-paymentBill`) { const result = evaluateAccountingFormula(e.target.value); if (result !== null) handleBlur(rowId, 'paymentBill', result, row); setActiveEditCell(null); } }} />
                    </td>
                    <td className="text-end p-0">
                      <Form.Control readOnly={activeEditCell !== `${rowId}-netAgent`} onClick={() => activateEditCell(rowId, 'netAgent')} onDoubleClick={() => activateEditCell(rowId, 'netAgent')} type="text" size="sm" className={`text-end border-0 bg-transparent h-100 ${activeEditCell === `${rowId}-netAgent` ? "bg-white shadow-sm" : ""}`} style={{ minWidth: '80px', boxShadow: 'none' }}
                        value={activeEditCell === `${rowId}-netAgent` ? (editedRows[rowId]?.netAgentFormula ?? netAgent) : netAgent.toLocaleString('id-ID')} data-row={index} data-col="netAgent"
                        data-cell={`${rowId}-netAgent`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-netAgent` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-netAgent` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'netAgent', rowId, row)}
                        onChange={(e) => { const formula = e.target.value; const result = evaluateAccountingFormula(formula); setEditedRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], netAgent: result ?? 0, netAgentFormula: formula } })) }}
                        onBlur={(e) => { if (activeEditCell === `${rowId}-netAgent`) { const result = evaluateAccountingFormula(e.target.value); if (result !== null) handleBlur(rowId, 'netAgent', result, row); setActiveEditCell(null); } }} />
                    </td>
                    <td className="text-end p-0">
                      <Form.Control readOnly={activeEditCell !== `${rowId}-susukAgent`} onClick={() => activateEditCell(rowId, 'susukAgent')} onDoubleClick={() => activateEditCell(rowId, 'susukAgent')} type="text" size="sm" className={`text-end border-0 bg-transparent h-100 ${activeEditCell === `${rowId}-susukAgent` ? "bg-white shadow-sm" : ""}`} style={{ minWidth: '80px', boxShadow: 'none' }}
                        value={activeEditCell === `${rowId}-susukAgent` ? susukAgent : susukAgent.toLocaleString('id-ID')} data-row={index} data-col="susukAgent"
                        data-cell={`${rowId}-susukAgent`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-susukAgent` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-susukAgent` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'susukAgent', rowId, row)}
                        onChange={(e) => { const rawVal = e.target.value.replace(/[^0-9]/g, ''); setEditedRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], susukAgent: parseInt(rawVal) || 0 } })) }}
                        onBlur={(e) => { if (activeEditCell === `${rowId}-susukAgent`) { const rawVal = e.target.value.replace(/[^0-9]/g, ''); handleBlur(rowId, 'susukAgent', parseInt(rawVal) || 0, row); setActiveEditCell(null); } }} />
                    </td>
                    <td className="text-end p-0">
                      <Form.Control 
                        readOnly={activeEditCell !== `${rowId}-netKuber`}
                        onClick={() => activateEditCell(rowId, 'netKuber')}
                        onDoubleClick={() => activateEditCell(rowId, 'netKuber')}
                        type="text" 
                        size="sm" 
                        className={`text-end border-0 bg-transparent h-100 ${activeEditCell === `${rowId}-netKuber` ? 'bg-white shadow-sm' : ''}`}
                        style={{ minWidth: '80px', boxShadow: 'none' }}
                        value={activeEditCell === `${rowId}-netKuber` ? (editedRows[rowId]?.netKuberFormula ?? netKuber) : netKuber.toLocaleString('id-ID')}
                        data-row={index}
                        data-col="netKuber"
                        data-cell={`${rowId}-netKuber`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-netKuber` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-netKuber` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'netKuber', rowId, row)}
                        onChange={(e) => {
                          const formula = e.target.value;
                          const result = evaluateAccountingFormula(formula);
                          setEditedRows(prev => ({
                            ...prev,
                            [rowId]: { ...prev[rowId], netKuber: result ?? 0, netKuberFormula: formula }
                          }))
                        }}
                        onBlur={(e) => {
                          if (activeEditCell === `${rowId}-netKuber`) {
                            const result = evaluateAccountingFormula(e.target.value);
                            if (result !== null) handleBlur(rowId, 'netKuber', result, row);
                            setActiveEditCell(null);
                          }
                        }}
                      />
                    </td>
                    <td className="text-center fw-bold">{totalMotor}</td>
                    <td className="text-end p-0">
                      <Form.Control readOnly={activeEditCell !== `${rowId}-others`} onClick={() => activateEditCell(rowId, 'others')} onDoubleClick={() => activateEditCell(rowId, 'others')} type="text" size="sm" className={`text-end border-0 bg-transparent h-100 ${activeEditCell === `${rowId}-others` ? "bg-white shadow-sm" : ""}`} style={{ minWidth: '80px', boxShadow: 'none' }}
                        value={activeEditCell === `${rowId}-others` ? others : others.toLocaleString('id-ID')} data-row={index} data-col="others"
                        data-cell={`${rowId}-others`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-others` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-others` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'others', rowId, row)}
                        onChange={(e) => { const rawVal = e.target.value.replace(/[^0-9]/g, ''); setEditedRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], others: parseInt(rawVal) || 0 } })) }}
                        onBlur={(e) => { if (activeEditCell === `${rowId}-others`) { const rawVal = e.target.value.replace(/[^0-9]/g, ''); handleBlur(rowId, 'others', parseInt(rawVal) || 0, row); setActiveEditCell(null); } }} />
                    </td>
                    <td className="text-center p-0">
                      <Form.Control readOnly={activeEditCell !== `${rowId}-pj`} onClick={() => activateEditCell(rowId, 'pj')} onDoubleClick={() => activateEditCell(rowId, 'pj')} 
                        type="text" 
                        inputMode="numeric"
                        size="sm" 
                        className={`text-center fw-bold border-0 bg-transparent h-100 px-1 ${activeEditCell === `${rowId}-pj` ? "bg-white shadow-sm" : ""}`}
                        style={{ width: '40px', minWidth: '40px', margin: '0 auto' }}
                        value={pjQty}
                        data-row={index}
                        data-col="pj"
                        data-cell={`${rowId}-pj`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-pj` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-pj` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'pj', rowId, row)}
                        onChange={(e) => { const rawVal = e.target.value.replace(/[^0-9]/g, ''); setEditedRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], pj: parseInt(rawVal) || 0, bmmm: 0 } })) }}
                        onBlur={(e) => { if (activeEditCell === `${rowId}-pj`) { const rawVal = e.target.value.replace(/[^0-9]/g, ''); handleBlur(rowId, 'pj', parseInt(rawVal) || 0, row); setActiveEditCell(null); } }}
                      />
                    </td>
                    <td className="text-center p-0">
                      <Form.Control readOnly={activeEditCell !== `${rowId}-bmmm`} onClick={() => activateEditCell(rowId, 'bmmm')} onDoubleClick={() => activateEditCell(rowId, 'bmmm')} 
                        type="text"
                        inputMode="numeric" 
                        size="sm" 
                        className={`text-center fw-bold border-0 bg-transparent h-100 px-1 ${activeEditCell === `${rowId}-bmmm` ? "bg-white shadow-sm" : ""}`}
                        style={{ width: '40px', minWidth: '40px', margin: '0 auto' }}
                        value={bmmmQty}
                        data-row={index}
                        data-col="bmmm"
                        data-cell={`${rowId}-bmmm`}
                        onMouseUp={(e) => activeEditCell === `${rowId}-bmmm` && e.preventDefault()}
                        onFocus={(e) => activeEditCell === `${rowId}-bmmm` && e.currentTarget.select()}
                        onKeyDown={(e) => handleKeyDown(e, index, 'bmmm', rowId, row)}
                        onChange={(e) => { const rawVal = e.target.value.replace(/[^0-9]/g, ''); setEditedRows(prev => ({ ...prev, [rowId]: { ...prev[rowId], bmmm: parseInt(rawVal) || 0, pj: 0 } })) }}
                        onBlur={(e) => { if (activeEditCell === `${rowId}-bmmm`) { const rawVal = e.target.value.replace(/[^0-9]/g, ''); handleBlur(rowId, 'bmmm', parseInt(rawVal) || 0, row); setActiveEditCell(null); } }}
                      />
                    </td>
                    <td className="text-end">{formatPrice(valPJ)}</td>
                    <td className="text-end">{formatPrice(valBMMM)}</td>
                    <td className="text-end bg-light">{formatPrice(calculatedNetAgent)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={14} className="text-center py-3 text-muted">No transactions for this date</td>
              </tr>
            )}
            {/* Total Row */}
            <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#c6e0b4' }}>
              <td colSpan={3} className="text-center border-dark">TOTAL</td>
              <td className="text-end border-dark" title="Total Payment Bill">{formatPrice(total?.payment_bill || 0)}</td>
              <td className="text-end border-dark" title="Total Net Agent">{formatPrice(total?.net_agent || 0)}</td>
              <td className="text-end border-dark" style={{ backgroundColor: '#c6e0b4' }} title="Total Susuk Agent">{formatPrice(total?.susuk_agent || 0)}</td>
              <td className="text-end border-dark text-black" style={{ backgroundColor: '#00bfff' }} title="Total Net Kuber">
                {formatPrice(tableData.reduce((acc, row) => acc + (getManualNetKuberValue(row, editedRows)), 0))}
              </td>
              <td className="text-center border-dark" title="Total Motor">
                {tableData.reduce((acc, row) => {
                  let motor = 0;
                  row.activities?.forEach(act => {
                    if (act.activity?.includes("ATV")) motor += act.qty;
                  });
                  return acc + motor;
                }, 0)}
              </td>
              <td className="text-end border-dark text-black" style={{ backgroundColor: '#00bfff' }}>{formatPrice(total?.others || 0)}</td>
              <td className="text-center border-dark">
                {tableData.reduce((acc, row) => {
                  const totalMotor = row.activities?.reduce((sum, act) => act.activity?.includes("ATV") ? sum + act.qty : sum, 0) || 0;
                  const defaultQty = getStoredPjQty(row, totalMotor);
                  const qty = editedRows[row.id]?.pj ?? defaultQty;
                  return acc + qty;
                }, 0)}
              </td>
              <td className="text-center border-dark">
                {tableData.reduce((acc, row) => {
                  const totalMotor = row.activities?.reduce((sum, act) => act.activity?.includes("ATV") ? sum + act.qty : sum, 0) || 0;
                  const defaultQty = getStoredBmmmQty(row, totalMotor);
                  const qty = editedRows[row.id]?.bmmm ?? defaultQty;
                  return acc + qty;
                }, 0)}
              </td>
              <td className="text-end border-dark text-black" style={{ backgroundColor: '#e6b8af' }}>
                {formatPrice(tableData.reduce((acc, row) => {
                  const totalMotor = row.activities?.reduce((sum, act) => act.activity?.includes("ATV") ? sum + act.qty : sum, 0) || 0;
                  const defaultQty = getStoredPjQty(row, totalMotor);
                  const qty = editedRows[row.id]?.pj ?? defaultQty;
                  return acc + (qty * 25000);
                }, 0))}
              </td>
              <td className="text-end border-dark text-white" style={{ backgroundColor: '#cc4125' }}>
                {formatPrice(tableData.reduce((acc, row) => {
                  const totalMotor = row.activities?.reduce((sum, act) => act.activity?.includes("ATV") ? sum + act.qty : sum, 0) || 0;
                  const defaultQty = getStoredBmmmQty(row, totalMotor);
                  const qty = editedRows[row.id]?.bmmm ?? defaultQty;
                  return acc + (qty * 10000);
                }, 0))}
              </td>
              <td className="text-end border-dark text-black" style={{ backgroundColor: '#ffc000' }}>
                {formatPrice(tableData.reduce((acc, row) => {
                  const netKuber = getManualNetKuberValue(row, editedRows);
                  const others = editedRows[row.id]?.others ?? (Number(row.others) || 0);
                  const totalMotor = row.activities?.reduce((sum, act) => act.activity?.includes("ATV") ? sum + act.qty : sum, 0) || 0;
                  const defaultPj = getStoredPjQty(row, totalMotor);
                  const pjQty = editedRows[row.id]?.pj ?? defaultPj;
                  const defaultBmmm = getStoredBmmmQty(row, totalMotor);
                  const bmmmQty = editedRows[row.id]?.bmmm ?? defaultBmmm;
                  const calculatedNetAgent = netKuber + others + (pjQty * 25000) + (bmmmQty * 10000);
                  return acc + (editedRows[row.id]?.netAgent !== undefined ? editedRows[row.id].netAgent : calculatedNetAgent);
                }, 0))}
              </td>
            </tr>
            {/* ATV VOUCHER ROW */}
            <tr className="fw-bold border-dark">
              <td colSpan={8} className="text-start text-white border-dark" style={{ backgroundColor: '#000000', fontStyle: 'italic' }}>ATV VOUCHER</td>
              <td 
                className="text-center text-black border-dark" 
                style={{ backgroundColor: '#b4a7d6' }} 
                title="Total Net Agent Kiri - Total Net Agent Kanan"
              >
                {formatPrice(totalNetAgentKiri - totalNetAgentKanan)}
              </td>
              <td className="border-dark bg-white"></td>
              <td className="border-dark bg-white"></td>
              <td 
                colSpan={2} 
                className="text-center text-black border-dark" 
                style={{ backgroundColor: '#00bfff' }} 
                title="Total PJ (Rp) + Total BM/MM (Rp)"
              >
                {formatPrice(totalPjRp + totalBmmmRp)}
              </td>
              <td className="border-dark bg-white"></td>
            </tr>

            {/* ================= DISCOVA SECTION ================= */}
            <tr className="accounting-hidden-section">
              <th colSpan={14} className="text-start text-black border-dark" style={{ backgroundColor: '#ffff00', fontStyle: 'italic', paddingLeft: '10px' }}>DISCOVA</th>
            </tr>
            {extraSections.discova.map((item) => (
              <tr key={item.id} className="accounting-hidden-section">
                <td className="border-dark bg-light"></td>
                <td className="text-center border-dark">{formattedDateTitle}</td>
                <td className="border-dark d-flex justify-content-between align-items-center">
                  <Form.Control type="text" size="sm" className="border-0 bg-transparent p-0 flex-grow-1" value={item.name} onChange={(e) => handleExtraSectionChange('discova', item.id, 'name', e.target.value)} />
                  <i className="fe fe-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeExtraSectionRow('discova', item.id)}></i>
                </td>
                <td className="text-end border-dark"><Form.Control type="text" size="sm" className="text-end border-0 bg-transparent p-0" value={item.col1} onChange={(e) => handleExtraSectionChange('discova', item.id, 'col1', e.target.value)} /></td>
                <td className="text-end border-dark"><Form.Control type="text" size="sm" className="text-end border-0 bg-transparent p-0" value={item.col2} onChange={(e) => handleExtraSectionChange('discova', item.id, 'col2', e.target.value)} /></td>
                <td className="text-end border-dark"><Form.Control type="text" size="sm" className="text-end border-0 bg-transparent p-0" value={item.col3} onChange={(e) => handleExtraSectionChange('discova', item.id, 'col3', e.target.value)} /></td>
                <td className="text-end border-dark"><Form.Control type="text" size="sm" className="text-end border-0 bg-transparent p-0" value={item.col4} onChange={(e) => handleExtraSectionChange('discova', item.id, 'col4', e.target.value)} /></td>
                <td className="text-end border-dark"><Form.Control type="text" size="sm" className="text-end border-0 bg-transparent p-0" value={item.col5} onChange={(e) => handleExtraSectionChange('discova', item.id, 'col5', e.target.value)} /></td>
                <td colSpan={6} className="border-dark bg-white"></td>
              </tr>
            ))}
            <tr className="accounting-hidden-section">
              <td colSpan={14} className="text-center border-dark py-1" style={{ backgroundColor: '#f8f9fa' }}>
                <Button variant="link" size="sm" className="text-primary p-0 m-0 text-decoration-none" onClick={() => addExtraSectionRow('discova')}>
                  <i className="fe fe-plus-circle me-1"></i> Tambah Transaksi
                </Button>
              </td>
            </tr>
            <tr className="fw-bold text-black border-dark accounting-hidden-section" style={{ backgroundColor: '#99cc00' }}>
              <td colSpan={4} className="text-center border-dark">TOTAL</td>
              <td className="text-end border-dark">{formatPrice(getExtraSectionTotal('discova', 'col2'))}</td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td colSpan={6} className="border-dark bg-white"></td>
            </tr>

            {/* ================= PROFIT RAFTING+TUBING+SWING SECTION ================= */}
            <tr>
              <th colSpan={3} className="text-start text-white border-dark" style={{ backgroundColor: '#000000', fontStyle: 'italic', paddingLeft: '10px' }}>PROFIT RAFTING+TUBING+SWING</th>
              <th className="text-center text-black border-dark" style={{ backgroundColor: '#ffff00', fontStyle: 'italic' }}>BATI</th>
              <th className="text-center text-black border-dark" style={{ backgroundColor: '#ffff00', fontStyle: 'italic' }}>RAFTING</th>
              <td colSpan={10} className="border-dark bg-white"></td>
            </tr>
            {extraSections.profit.map((item) => (
              <tr key={item.id}>
                <td className="border-dark bg-light"></td>
                <td className="text-center border-dark">{formattedDateTitle}</td>
                <td className="border-dark d-flex justify-content-between align-items-center" colSpan={2}>
                  <Form.Control type="text" size="sm" className="border-0 bg-transparent p-0 flex-grow-1" placeholder="Contoh: Bati / Rafting / Tubing / Swing" value={item.name} onChange={(e) => handleExtraSectionChange('profit', item.id, 'name', e.target.value)} onBlur={() => saveExtraSectionRow('profit', item)} />
                  <i className="fe fe-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeExtraSectionRow('profit', item.id)}></i>
                </td>
                <td className="text-end border-dark" style={{ backgroundColor: '#fce4d6' }}><Form.Control type="text" inputMode="numeric" size="sm" className="text-end border-0 bg-transparent p-0" placeholder="Nominal" value={getExtraCurrencyValue('profit', item, 'col2')} data-extra-cell={`profit-${item.id}-col2`} onClick={() => activateExtraCell('profit', item.id, 'col2')} onChange={(e) => handleExtraSectionChange('profit', item.id, 'col2', parseNumberInput(e.target.value))} onKeyDown={(e) => handleExtraCurrencyKeyDown(e, 'profit', item)} onBlur={() => { saveExtraSectionRow('profit', item); setActiveExtraCell(null); }} disabled={extraSectionSaving[`profit-${item.id}`]} /></td>
                <td colSpan={3} className="border-dark bg-white"></td>
                <td colSpan={6} className="border-dark bg-white"></td>
              </tr>
            ))}
            <tr>
              <td colSpan={14} className="text-center border-dark py-1" style={{ backgroundColor: '#f8f9fa' }}>
                <Button variant="link" size="sm" className="text-primary p-0 m-0 text-decoration-none" onClick={() => addExtraSectionRow('profit')}>
                  <i className="fe fe-plus-circle me-1"></i> Tambah Transaksi
                </Button>
              </td>
            </tr>
            <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#99cc00' }}>
              <td colSpan={4} className="text-center border-dark">TOTAL</td>
              <td className="text-end border-dark">{formatPrice(getExtraSectionTotal('profit', 'col2'))}</td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td colSpan={6} className="border-dark bg-white"></td>
            </tr>

            {/* ================= LAIN-LAIN SECTION ================= */}
            <tr>
              <th colSpan={14} className="text-start text-white border-dark" style={{ backgroundColor: '#000000', fontStyle: 'italic', paddingLeft: '10px' }}>LAIN-LAIN</th>
            </tr>
            {extraSections.lainLain.map((item) => (
              <tr key={item.id}>
                <td className="border-dark bg-light"></td>
                <td className="text-center border-dark">{formattedDateTitle}</td>
                <td className="border-dark d-flex justify-content-between align-items-center" style={{ backgroundColor: '#e6b8af' }}>
                  <Form.Control type="text" size="sm" className="border-0 bg-transparent p-0 flex-grow-1" placeholder="Keterangan..." value={item.name} onChange={(e) => handleExtraSectionChange('lainLain', item.id, 'name', e.target.value)} onBlur={() => saveExtraSectionRow('lainLain', item)} />
                  <i className="fe fe-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeExtraSectionRow('lainLain', item.id)}></i>
                </td>
                <td className="border-dark bg-white"></td>
                <td className="text-end border-dark" style={{ backgroundColor: '#fce4d6' }}><Form.Control type="text" inputMode="numeric" size="sm" className="text-end border-0 bg-transparent p-0" placeholder="Nominal" value={getExtraCurrencyValue('lainLain', item, 'col2')} data-extra-cell={`lainLain-${item.id}-col2`} onClick={() => activateExtraCell('lainLain', item.id, 'col2')} onChange={(e) => handleExtraSectionChange('lainLain', item.id, 'col2', parseNumberInput(e.target.value))} onKeyDown={(e) => handleExtraCurrencyKeyDown(e, 'lainLain', item)} onBlur={() => { saveExtraSectionRow('lainLain', item); setActiveExtraCell(null); }} disabled={extraSectionSaving[`lainLain-${item.id}`]} /></td>
                <td colSpan={3} className="border-dark bg-white"></td>
                <td colSpan={6} className="border-dark bg-white"></td>
              </tr>
            ))}
            <tr>
              <td colSpan={14} className="text-center border-dark py-1" style={{ backgroundColor: '#f8f9fa' }}>
                <Button variant="link" size="sm" className="text-primary p-0 m-0 text-decoration-none" onClick={() => addExtraSectionRow('lainLain')}>
                  <i className="fe fe-plus-circle me-1"></i> Tambah Transaksi
                </Button>
              </td>
            </tr>
            <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#99cc00' }}>
              <td colSpan={4} className="text-center border-dark">TOTAL</td>
              <td className="text-end border-dark">{formatPrice(getExtraSectionTotal('lainLain', 'col2'))}</td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td colSpan={6} className="border-dark bg-white"></td>
            </tr>

            {/* ================= BIAYA TRANSPORT SECTION ================= */}
            <tr>
              <th colSpan={14} className="text-start text-white border-dark" style={{ backgroundColor: '#000000', fontStyle: 'italic', paddingLeft: '10px' }}>BIAYA TRANSPORT</th>
            </tr>
            {extraSections.transport.map((item) => (
              <tr key={item.id}>
                <td className="border-dark bg-light"></td>
                <td className="text-center border-dark">{formattedDateTitle}</td>
                <td className="border-dark d-flex justify-content-between align-items-center">
                  <Form.Control type="text" size="sm" className="border-0 bg-transparent p-0 flex-grow-1" placeholder="Keterangan transport..." value={item.name} onChange={(e) => handleExtraSectionChange('transport', item.id, 'name', e.target.value)} onBlur={() => saveExtraSectionRow('transport', item)} />
                  <i className="fe fe-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeExtraSectionRow('transport', item.id)}></i>
                </td>
                <td className="border-dark bg-white"></td>
                <td className="text-end border-dark" style={{ backgroundColor: '#fce4d6' }}><Form.Control type="text" inputMode="numeric" size="sm" className="text-end border-0 bg-transparent p-0" placeholder="Nominal" value={getExtraCurrencyValue('transport', item, 'col2')} data-extra-cell={`transport-${item.id}-col2`} onClick={() => activateExtraCell('transport', item.id, 'col2')} onChange={(e) => handleExtraSectionChange('transport', item.id, 'col2', parseNumberInput(e.target.value))} onKeyDown={(e) => handleExtraCurrencyKeyDown(e, 'transport', item)} onBlur={() => { saveExtraSectionRow('transport', item); setActiveExtraCell(null); }} disabled={extraSectionSaving[`transport-${item.id}`]} /></td>
                <td colSpan={3} className="border-dark bg-white"></td>
                <td colSpan={6} className="border-dark bg-white"></td>
              </tr>
            ))}
            <tr>
              <td colSpan={14} className="text-center border-dark py-1" style={{ backgroundColor: '#f8f9fa' }}>
                <Button variant="link" size="sm" className="text-primary p-0 m-0 text-decoration-none" onClick={() => addExtraSectionRow('transport')}>
                  <i className="fe fe-plus-circle me-1"></i> Tambah Transaksi
                </Button>
              </td>
            </tr>
            <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#99cc00' }}>
              <td colSpan={4} className="text-center border-dark">TOTAL</td>
              <td className="text-end border-dark">{formatPrice(getExtraSectionTotal('transport', 'col2'))}</td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td colSpan={6} className="border-dark bg-white"></td>
            </tr>

            {/* ================= BIAYA - BIAYA SECTION ================= */}
            <tr>
              <th colSpan={14} className="text-start text-white border-dark" style={{ backgroundColor: '#000000', fontStyle: 'italic', paddingLeft: '10px' }}>BIAYA - BIAYA</th>
            </tr>
            {extraSections.biaya.map((item) => (
              <tr key={item.id}>
                <td className="border-dark bg-light"></td>
                <td className="text-center border-dark">{formattedDateTitle}</td>
                <td className="border-dark d-flex justify-content-between align-items-center">
	                  <Form.Control type="text" size="sm" className="border-0 bg-transparent p-0 flex-grow-1" placeholder="Keterangan..." value={item.name} onChange={(e) => handleExtraSectionChange('biaya', item.id, 'name', e.target.value)} onBlur={() => saveExtraSectionRow('biaya', item)} />
                  <i className="fe fe-trash text-danger" style={{ cursor: 'pointer' }} onClick={() => removeExtraSectionRow('biaya', item.id)}></i>
                </td>
                <td className="border-dark bg-white"></td>
	                <td className="text-end border-dark"><Form.Control type="text" inputMode="numeric" size="sm" className="text-end border-0 bg-transparent p-0" placeholder="Nominal" value={getExtraCurrencyValue('biaya', item, 'col2')} data-extra-cell={`biaya-${item.id}-col2`} onClick={() => activateExtraCell('biaya', item.id, 'col2')} onChange={(e) => handleExtraSectionChange('biaya', item.id, 'col2', parseNumberInput(e.target.value))} onKeyDown={(e) => handleExtraCurrencyKeyDown(e, 'biaya', item)} onBlur={() => { saveExtraSectionRow('biaya', item); setActiveExtraCell(null); }} disabled={extraSectionSaving[`biaya-${item.id}`]} /></td>
                <td colSpan={3} className="border-dark bg-white"></td>
                <td colSpan={6} className="border-dark bg-white"></td>
              </tr>
            ))}
            <tr>
              <td colSpan={14} className="text-center border-dark py-1" style={{ backgroundColor: '#f8f9fa' }}>
                <Button variant="link" size="sm" className="text-primary p-0 m-0 text-decoration-none" onClick={() => addExtraSectionRow('biaya')}>
                  <i className="fe fe-plus-circle me-1"></i> Tambah Transaksi
                </Button>
              </td>
            </tr>
            <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#99cc00' }}>
              <td colSpan={4} className="text-center border-dark">TOTAL</td>
              <td className="text-end border-dark">{formatPrice(getExtraSectionTotal('biaya', 'col2'))}</td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td className="border-dark"></td>
              <td colSpan={6} className="border-dark bg-white"></td>
            </tr>
          </tbody>
        </table>
        </div>

        {/* Additional Sections */}
        {/* <table className="accounting-v2-table mt-3" style={{ width: "100%", minWidth: "1200px" }}>
          <tbody>
            <tr><th className="table-header-black text-start" colSpan={7}>ATV VOUCHER</th></tr>
            <tr><th className="table-header-black text-start text-warning" colSpan={7}>DISCOVA</th></tr>
            <tr><th className="table-header-black text-start text-warning" colSpan={7}>PROFIT RAFTING+TUBING+SWING</th></tr>
            <tr><th className="table-header-black text-start text-warning" colSpan={7}>LAIN-LAIN</th></tr>
            <tr><th className="table-header-black text-start text-warning" colSpan={7}>BIAYA TRANSPORT</th></tr>
            <tr><th className="table-header-black text-start text-warning" colSpan={7}>BIAYA - BIAYA</th></tr>
          </tbody>
        </table> */}

        {/* table Resume Transaksi */}
        <div className="mt-5" style={{ overflowX: 'auto' }}>
          {(() => {
            const edcGroups = {};
            tableData.forEach(reservation => {
              const paymentItems = reservation.reservation_income?.payment_items || [];
              paymentItems.forEach(item => {
                const pmName = item.payment_method?.name || "UNKNOWN EDC";
                if (!edcGroups[pmName]) edcGroups[pmName] = [];
                edcGroups[pmName].push({
                  reservation,
                  paymentItem: item
                });
              });
            });

            return Object.entries(edcGroups).map(([pmName, items]) => {
              const sortedItems = sortByCreatedAsc(items.map((item) => ({
                ...item,
                created_at: item.paymentItem?.created_at || item.reservation?.created_at,
              })));
              let totalNetAgent = 0;
              let totalCharge = 0;

              return (
                <div key={pmName} className="mb-4">
                  <table className="accounting-v2-table w-100">
                    <thead>
                      <tr>
                        <th className="table-header-black text-start px-2" colSpan={6}>
                          <h4 className="m-0 fw-bold fst-italic text-uppercase" style={{ fontSize: '14px' }}>
                            {pmName}
                          </h4>
                        </th>
                      </tr>
                      <tr>
                        <th className="bg-light">TGL</th>
                        <th className="bg-light">TRANSAKSI</th>
                        <th className="bg-light text-center">PAYMENT</th>
                        <th className="bg-light text-center">%</th>
                        <th className="bg-light text-center">TAX</th>
                        <th className="bg-light text-center">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((row, idx) => {
                        const { reservation, paymentItem } = row;
                        
                        const paymentId = paymentItem.id;
                        // Parse amounts
                        const rawAmount = editedPayments[paymentId] !== undefined ? editedPayments[paymentId] : (Number(paymentItem.amount) || 0);
                        const taxRate = editedPaymentTaxRates[paymentId] ?? getPaymentTaxRate(paymentItem);
                        const charge = getPaymentTaxAmount(paymentItem, rawAmount, editedPaymentTaxes, editedPaymentTaxRates);
                        const paymentEdited = isPaymentItemEdited(
                          paymentItem,
                          reservation,
                          editedPayments,
                          editedPaymentTaxes,
                          editedPaymentTaxRates
                        );
                        
                        totalNetAgent += rawAmount;
                        totalCharge += charge;
                        
                        // Formatter for transaction string
                        let singleQty = 0;
                        let tandemQty = 0;
                        reservation.activities?.forEach(act => {
                          if (act.activity === "ATV Single") singleQty += act.qty;
                          if (act.activity === "ATV Tandem") tandemQty += act.qty;
                        });
                        let qtyStr = [];
                        if (singleQty > 0) qtyStr.push(`${singleQty} SINGLE`);
                        if (tandemQty > 0) qtyStr.push(`${tandemQty} TANDEM`);
                        const typeLabel = getAccountingTypeLabel(reservation);
                        const typeStr = typeLabel ? `/${typeLabel}` : '';
                        const guestTransport = reservation.guest_transport ? `/${reservation.guest_transport.toUpperCase()}` : '';
                        const hotel = reservation.hotel ? `/${reservation.hotel.toUpperCase()}` : '';
                        const transaksiText = `ATV ${qtyStr.join(',')}${typeStr}${guestTransport}/${reservation.guest_name?.toUpperCase()}${hotel}/${reservation.agent_name?.toUpperCase()}`;
                        
                        // Date formatting
                        const resDate = reservation.reservation_date ? new Date(reservation.reservation_date) : new Date();
                        const shortDate = resDate.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "2-digit" }).toUpperCase();
                        
                        return (
                          <React.Fragment key={idx}>
                            <tr className={paymentEdited ? "accounting-row-edited" : ""}>
                              <td className="text-center">
                                {paymentEdited && <span className="accounting-edited-check" title="Payment sudah diedit">✓</span>}
                                {shortDate}
                              </td>
                              <td
                                style={{ cursor: "pointer" }}
                                title="Double click untuk membuka detail payment"
                                onDoubleClick={() => handleOpenEditPaymentModal(paymentItem, transaksiText)}
                              >
                                {transaksiText}
                              </td>
                              <td className="text-end p-0" title="Klik untuk mengedit">
                                <Form.Control 
                                  type="text" 
                                  size="sm" 
                                  className="text-end border-0 bg-transparent h-100" 
                                  style={{ minWidth: '80px', boxShadow: 'none' }}
                                  value={rawAmount.toLocaleString('id-ID')}
                                  onFocus={(event) => event.currentTarget.select()}
                                  onChange={(e) => {
                                    setEditedPayments(prev => ({ ...prev, [paymentId]: parseNumberInput(e.target.value) }));
                                  }}
                                  onKeyDown={handlePaymentKeyDown}
                                  onBlur={(e) => {
                                    handlePaymentBlur(paymentItem, parseNumberInput(e.target.value));
                                  }}
                                />
                              </td>
                              <td className="text-end p-0" title="Klik untuk mengedit persen tax">
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  className="text-end border-0 bg-transparent h-100"
                                  style={{ minWidth: '52px', boxShadow: 'none' }}
                                  value={taxRate}
                                  onFocus={(event) => event.currentTarget.select()}
                                  onChange={(e) => {
                                    const nextRate = parseNumberInput(e.target.value);
                                    setEditedPaymentTaxRates(prev => ({ ...prev, [paymentId]: nextRate }));
                                    setEditedPaymentTaxes(prev => {
                                      const next = { ...prev };
                                      delete next[paymentId];
                                      return next;
                                    });
                                  }}
                                  onKeyDown={handlePaymentKeyDown}
                                  onBlur={(e) => handlePaymentTaxRateBlur(paymentItem, parseNumberInput(e.target.value))}
                                />
                              </td>
                              <td className="text-end p-0" title="Klik untuk mengedit tax">
                                <Form.Control
                                  type="text"
                                  size="sm"
                                  className="text-end border-0 bg-transparent h-100"
                                  style={{ minWidth: '60px', boxShadow: 'none' }}
                                  value={formatPrice(charge)}
                                  onFocus={(event) => event.currentTarget.select()}
                                  onChange={(e) => {
                                    const nextTaxAmount = parseNumberInput(e.target.value);
                                    setEditedPaymentTaxes(prev => ({ ...prev, [paymentId]: nextTaxAmount }));
                                    setEditedPaymentTaxRates(prev => ({
                                      ...prev,
                                      [paymentId]: getPaymentTaxRateFromAmount(rawAmount, nextTaxAmount),
                                    }));
                                  }}
                                  onKeyDown={handlePaymentKeyDown}
                                  onBlur={(e) => handlePaymentTaxBlur(paymentItem, parseNumberInput(e.target.value))}
                                />
                              </td>
                              <td className="text-end fw-bold" title={`Kalkulasi: Payment (${formatPrice(rawAmount)}) + Tax (${formatPrice(charge)})`}>
                                {formatPrice(rawAmount + charge)}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                      <tr className="bg-green-light fw-bold">
	                        <td colSpan={2} className="text-center">TOTAL</td>
	                        <td className="text-end">{formatPrice(totalNetAgent)}</td>
	                        <td className="text-center">-</td>
	                        <td className="text-end">{formatPrice(totalCharge)}</td>
	                        <td className="text-end">{formatPrice(totalNetAgent + totalCharge)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            });
          })()}
        </div>

        <table className="resume-table mt-4" style={{ width: '400px', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead>
            <tr>
              <th colSpan={2} className="resume-table-header text-center text-black" style={{ border: '1px solid #000', fontSize: '18px', padding: '5px' }}>RESUME TRANSAKSI</th>
            </tr>
            <tr>
              <th colSpan={2} className="text-center" style={{ border: '1px solid #000', fontSize: '11px', padding: '3px', backgroundColor: isAccountingResumeFallback ? '#fff3bf' : '#d3f9d8', color: isAccountingResumeFallback ? '#8a5a00' : '#2b8a3e' }}>
                {isAccountingResumeFallback ? "FE FALLBACK" : "API RESUME"}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="fw-bold fst-italic text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>ATV</td>
              <td className="text-end text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>
                {formatPrice(resumeRevenueAtv)}
              </td>
            </tr>
            <tr>
              <td className="fw-bold fst-italic text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>TRANSPORT</td>
              <td className="text-end text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>
                {formatPrice(resumeTransport)}
              </td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '3px 8px' }}>&nbsp;</td>
              <td className="text-end text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>0</td>
            </tr>
            <tr className="fw-bold text-black">
              <td className="text-center fst-italic" style={{ border: '1px solid #000', padding: '3px 8px' }}>REVENUE ATV</td>
              <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px' }}>
                {formatPrice(resumeRevenueAtv)}
              </td>
            </tr>
            <tr>
              <td className="fw-bold fst-italic text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>PROFIT LAIN ACTIVITIES</td>
              <td className="text-end text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>
                {formatPrice(resumeProfitLain)}
              </td>
            </tr>
            <tr>
              <td className="fw-bold fst-italic text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>TIPING</td>
              <td className="text-end text-black" style={{ border: '1px solid #000', padding: '3px 8px' }}>
                {formatPrice(resumeTipping)}
              </td>
            </tr>
            <tr className="fw-bold text-black">
              <td className="text-center fst-italic" style={{ border: '1px solid #000', padding: '3px 8px' }}>OTHER REVENUE</td>
              <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px' }}>{formatPrice(resumeOtherRevenue)}</td>
            </tr>
            <tr className="fw-bold text-black">
              <td className="text-center fst-italic" style={{ border: '1px solid #000', padding: '3px 8px' }}>BIAYA-BIAYA</td>
              <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px' }}>
                {formatPrice(resumeBiayaBiaya)}
              </td>
            </tr>
            
            {/* EDC by Payment Methods */}
            {accountingResumePayments.map((pm, index) => {
              const bgColors = ["#99cc00", "#ffff00", "#9fc5e8", "#f4cccc", "#d9ead3"]; // green, yellow, blue
              const bgColor = bgColors[index % bgColors.length];
              const paymentMethodName = pm.payment_method || pm.payment_method_name || pm.name || "Payment Method";
              const paymentTotal = Number(pm.gross_amount ?? pm.total_amount ?? pm.amount ?? pm.total_net_agent ?? 0) || 0;
              const paymentMethods = Array.isArray(allPaymentMethods) ? allPaymentMethods : (allPaymentMethods?.data || []);
              const methodDetails = paymentMethods.find(m =>
                String(m.name || '').toLowerCase() === String(paymentMethodName).toLowerCase()
              );
              const taxRate = Number(
                methodDetails?.charge_percent
                ?? methodDetails?.tax
                ?? pm.charge_percent
                ?? pm.tax
                ?? 0
              ) || 0;
              const chargeLabel = methodDetails?.charge_label || "CHARGE";
              const charge = Number(pm.total_payment_charge ?? pm.total_charge ?? pm.charge_amount ?? pm.tax_amount ?? ((paymentTotal * taxRate) / 100)) || 0;
              return (
                <React.Fragment key={`${paymentMethodName}-${index}`}>
                  <tr style={{ backgroundColor: bgColor }} className="text-black">
                    <td className="fw-bold" style={{ border: '1px solid #000', padding: '3px 8px' }}>EDC</td>
                    <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px' }}>{formatPrice(paymentTotal)}</td>
                  </tr>
                  <tr style={{ backgroundColor: bgColor }} className="text-black">
                    <td className="fw-bold fst-italic" style={{ border: '1px solid #000', padding: '3px 8px' }}>{paymentMethodName}</td>
                    <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px' }}>{formatPrice(paymentTotal)}</td>
                  </tr>
                  {taxRate > 0 && (
                    <tr style={{ backgroundColor: bgColor }} className="text-black">
                      <td className="fw-bold fst-italic" style={{ border: '1px solid #000', padding: '3px 8px' }}>{chargeLabel}</td>
                      <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px' }}>{formatPrice(charge)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            
            <tr className="fw-bold text-black" title="Net Revenue dari API accounting-resume">
              <td className="text-center" style={{ border: '1px solid #000', padding: '3px 8px', fontSize: '15px' }}>NET REVENUE</td>
              <td className="text-end" style={{ border: '1px solid #000', padding: '3px 8px', fontSize: '15px' }}>
                {formatPrice(resumeNetRevenue)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '3px 8px', borderRight: '1px solid #ddd' }}>&nbsp;</td>
              <td style={{ padding: '3px 8px' }}>&nbsp;</td>
            </tr>
            <tr className="fw-bold text-black">
              <td className="fst-italic" style={{ padding: '3px 8px', borderRight: '1px solid #ddd' }}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Cash on hand dari API accounting-resume.</Tooltip>}>
                  <span style={{ cursor: "help", borderBottom: "1px dashed #000" }}>CASH ON HAND</span>
                </OverlayTrigger>
              </td>
              <td className="text-end text-danger" style={{ padding: '3px 8px' }}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Cash on hand dari API accounting-resume.</Tooltip>}>
                  <span style={{ cursor: "help" }}>
                    {formatPrice(resumeCashOnHand)}
                  </span>
                </OverlayTrigger>
              </td>
            </tr>
            {extraSections.cashOnHand.map((item) => (
              <tr key={item.id} className="cash-on-hand-deduction-row">
                <td style={{ padding: '3px 8px', borderRight: '1px solid #ddd' }}>
                  <div className="d-flex align-items-center gap-2">
	                    <Form.Control type="text" size="sm" className="border-0 bg-transparent p-0" placeholder="Keterangan pengurangan..." value={item.name} onChange={(e) => handleExtraSectionChange('cashOnHand', item.id, 'name', e.target.value)} onBlur={() => saveExtraSectionRow('cashOnHand', item)} />
                    <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeExtraSectionRow('cashOnHand', item.id)} title="Hapus baris">×</button>
                  </div>
                </td>
                <td className="text-end fw-bold text-black" style={{ padding: '3px 8px' }}>
	                  <Form.Control type="text" inputMode="numeric" size="sm" className="text-end border-0 bg-transparent p-0" placeholder="Nominal" value={getExtraCurrencyValue('cashOnHand', item, 'col2')} data-extra-cell={`cashOnHand-${item.id}-col2`} onClick={() => activateExtraCell('cashOnHand', item.id, 'col2')} onChange={(e) => handleExtraSectionChange('cashOnHand', item.id, 'col2', parseNumberInput(e.target.value))} onKeyDown={(e) => handleExtraCurrencyKeyDown(e, 'cashOnHand', item)} onBlur={() => { saveExtraSectionRow('cashOnHand', item); setActiveExtraCell(null); }} disabled={extraSectionSaving[`cashOnHand-${item.id}`]} />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} className="text-center border-top-0" style={{ padding: '3px 8px' }}>
                <Button variant="link" size="sm" className="text-primary p-0 text-decoration-none" onClick={() => addExtraSectionRow('cashOnHand')}>+ Tambah Pengurangan</Button>
              </td>
            </tr>
            <tr className="fw-bold text-black">
              <td className="text-end" style={{ padding: '3px 8px' }}>TOTAL PENGURANGAN CASH ON HAND</td>
              <td className="text-end text-danger" style={{ padding: '3px 8px' }}>{formatPrice(getExtraSectionTotal('cashOnHand', 'col2'))}</td>
            </tr>
            <tr className="fw-bold text-black">
              <td className="text-center fst-italic" style={{ padding: '3px 8px', borderRight: '1px solid #ddd' }}>TOTAL</td>
              <td className="text-end text-danger" style={{ border: '2px solid #3c78d8', padding: '3px 8px' }}>
                {formatPrice(resumeCashOnHand)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered restoreFocus={false}>
        <Form onSubmit={handleSubmitPayment}>
          <Modal.Header closeButton>
            <Modal.Title>{paymentForm.id ? "Edit Payment EDC" : "Payment EDC"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {paymentForm.sourceRow && (
              <div className="mb-3 p-3 bg-light rounded border">
                <p className="mb-1 text-primary fw-bold" style={{ fontSize: '13px' }}>
                  {paymentForm.sourceRow.transaksiText || paymentForm.sourceRow.code}
                </p>
                <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                  <span className="text-muted small">Payment Bill:</span>
                  <span className="fw-bold">{formatPrice(Number(paymentForm.sourceRow.payment_bill) || 0)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Net Agent:</span>
                  <span className="fw-bold">{formatPrice(Number(paymentForm.sourceRow.net_agent) || 0)}</span>
                </div>
              </div>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Transaksi</Form.Label>
              <Form.Control type="text" value={paymentForm.sourceRow?.transaksiText || ""} disabled />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Payment Method <span className="text-danger">*</span></Form.Label>
              <Form.Select 
                required
                autoFocus
                value={paymentForm.payment_method_id}
                onChange={e => setPaymentForm(prev => ({ ...prev, payment_method_id: e.target.value }))}
              >
                <option value="">-- Select Payment Method --</option>
                {allPaymentMethods.map(pm => (
                  <option key={pm.id} value={pm.id}>{pm.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
              <Form.Control 
                type="text" 
                required
                placeholder="Enter amount"
                data-payment-amount
                value={paymentForm.amount === "" ? "" : Number(paymentForm.amount).toLocaleString('id-ID')}
                onChange={e => {
                  const rawVal = e.target.value.replace(/[^0-9]/g, '');
                  setPaymentForm(prev => ({ ...prev, amount: rawVal ? parseInt(rawVal) : "" }));
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <div>
              {paymentForm.reservation_income_id && !paymentForm.id && (
                <Button variant="danger" size="sm" onClick={handleDeleteIncome}>
                  <i className="fe fe-trash-2 me-1"></i> Hapus Transaksi
                </Button>
              )}
              {paymentForm.id && (
                <Button variant="danger" size="sm" onClick={handleDeletePayment}>
                  <i className="fe fe-trash-2 me-1"></i> Hapus Payment
                </Button>
              )}
              {paymentForm.sourceRow && getReservationIncomeId(paymentForm.sourceRow) && (
                <Button 
                  variant="warning" 
                  size="sm" 
                  className="ms-2"
                  onClick={() => {
                    handleOpenKeepModal(paymentForm.sourceRow);
                  }}
                >
                  <i className="fe fe-file-text me-1"></i> Keep
                </Button>
              )}
            </div>
            <div>
              <Button variant="secondary" size="sm" onClick={() => setShowPaymentModal(false)} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={createReservationPayment.isPending || updateReservationPayment.isPending}>
                {(createReservationPayment.isPending || updateReservationPayment.isPending) ? "Saving..." : "Save Payment"}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      <AccountingV2BookkeepingSyncModal
        show={showBookkeepingSync}
        date={selectedDate}
        values={bookkeepingSyncValues}
        hasUnsavedChanges={Object.keys(savingRows).length > 0}
        onHide={() => setShowBookkeepingSync(false)}
      />

      <AccountingV2TrfModal
        show={Boolean(trfModalRows)}
        rows={trfModalRows || []}
        onHide={() => setTrfModalRows(null)}
        onDone={() => {
          setTrfModalRows(null);
          setSelectedKeepRows({});
        }}
      />

      {/* Keep Modal */}
      <Modal show={showKeepModal} onHide={() => setShowKeepModal(false)} centered>
        <Form onSubmit={handleSubmitKeep}>
          <Modal.Header closeButton>
            <Modal.Title>Simpan ke Daftar Keep</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {keepProgress.active ? (
              <div className="upload-animation-container p-4 text-center">
                <i className="fe fe-upload-cloud upload-icon-bounce text-primary" style={{ fontSize: '48px', animation: 'bounceUpload 1.5s infinite' }}></i>
                <h5 className="mt-3">Menyimpan ke Keep...</h5>
                <div style={{ width: "80%", margin: "20px auto" }}>
                  <ProgressBar animated striped now={100} />
                </div>
                <p className="text-muted small">Harap tunggu, menyimpan {keepProgress.total} transaksi sekaligus.</p>
              </div>
            ) : (
              <>
                {keepForm.sourceRow && (
              <div className="mb-3 p-3 bg-light rounded border">
                <p className="mb-1 text-primary fw-bold" style={{ fontSize: '13px' }}>
                  {keepForm.sourceRows?.length > 1
                    ? `${keepForm.sourceRows.length} transaksi terpilih`
                    : (keepForm.sourceRow.transaksiText || keepForm.sourceRow.code)}
                </p>
                <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                  <span className="text-muted small">Payment Bill:</span>
                  <span className="fw-bold">{formatPrice(Number(keepForm.payment_bill) || 0)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Net Agent:</span>
                  <span className="fw-bold">{formatPrice(Number(keepForm.net_agent) || 0)}</span>
                </div>
              </div>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Master Keep (Koordinator) <span className="text-danger">*</span></Form.Label>
              {!showQuickCreate ? (
                <div className="d-flex gap-2">
                  <div style={{ flex: 1 }}>
                    <Select
                      options={masterKeepList.map(m => ({ value: m.id, label: m.name }))}
                      value={keepForm.master_keep_id ? { value: keepForm.master_keep_id, label: masterKeepList.find(m => m.id === keepForm.master_keep_id)?.name } : null}
                      onChange={selected => setKeepForm(prev => ({ ...prev, master_keep_id: selected ? selected.value : "" }))}
                      placeholder="-- Pilih Master Keep --"
                      isClearable
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                  <Button variant="outline-primary" onClick={() => setShowQuickCreate(true)} style={{ whiteSpace: 'nowrap' }}>
                    <i className="fe fe-plus"></i> Baru
                  </Button>
                </div>
              ) : (
                <div className="p-3 border rounded bg-light">
                  <Form.Label className="small fw-bold">Nama Koordinator Baru</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control 
                      type="text" 
                      placeholder="Masukkan nama..."
                      value={newMasterName}
                      onChange={e => setNewMasterName(e.target.value)}
                    />
                    <Button variant="success" onClick={handleCreateQuickMasterKeep} disabled={isCreatingMaster}>
                      {isCreatingMaster ? <Spinner size="sm" animation="border" /> : "Simpan"}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowQuickCreate(false)}>Batal</Button>
                  </div>
                </div>
              )}
              <Form.Text className="text-muted">
                Pilih entitas/koordinator yang bertanggung jawab atas tagihan ini (misal: Josh).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Catatan / Alasan Keep</Form.Label>
              <Form.Control 
                as="textarea"
                rows={3}
                placeholder="Misal: Ditagihkan besok siang ke agent..."
                value={keepForm.notes}
                onChange={e => setKeepForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Form.Group>
              </>
            )}
          </Modal.Body>
          {!keepProgress.active && (
            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="secondary" size="sm" onClick={() => setShowKeepModal(false)}>
                Batal
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={keepTransaction.isPending}>
                {keepTransaction.isPending ? "Menyimpan..." : "Simpan Keep"}
              </Button>
            </Modal.Footer>
          )}
        </Form>
      </Modal>

      {/* Bottom Sticky Bar */}
      <div className="accounting-v2-bottom-bar" style={{ marginLeft: 'var(--sidebar-width, 0)' }}>
        {/* Year Button as Date Picker Trigger */}
        <div 
          className="date-tab-year" 
          onClick={handleOpenDatePicker}
          title="Pilih tanggal custom"
        >
          {bottomTabYear}
        </div>

        <div className="bottom-date-selector">
          {pastDays.map((d) => {
            const dDateStr = d.toLocaleDateString("en-CA");
            const isActive = selectedDate === dDateStr;
            // Format tgl/bln
            const dDate = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
            
            return (
              <div 
                key={dDateStr} 
                className={`date-tab ${isActive ? 'active' : ''}`} 
                onClick={() => {
                  updateFilter("start_date", dDateStr);
                  updateFilter("end_date", dDateStr);
                }}
              >
                <span className="date-tab-day">{dDate}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Centered Modal for Date Picker */}
      <Modal show={showDatePicker} onHide={() => setShowDatePicker(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h5 mb-0">Pilih Tanggal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control 
            type="date" 
            value={tempDate}
            onChange={(e) => setTempDate(e.target.value)} 
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDatePicker(false)}>Batal</Button>
          <Button variant="primary" onClick={() => {
            if (tempDate) {
              updateFilter("start_date", tempDate);
              updateFilter("end_date", tempDate);
            }
            setShowDatePicker(false);
          }}>
            Pilih
          </Button>
        </Modal.Footer>
      </Modal>

    </LayoutDashboard>
  );
};

export default AccountingV2Page;
